import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useLocalization } from '@shared-ui/localization-context';
import { Viewport, ViewSmall, ViewLarge } from '@shared-ui/viewport-context';
import * as React from 'react';
import { E as EGDSInput, g as EGDSFakeInput, b as EGDSTextInput } from '../swapper-input/index-chunk.esm.js';
import { c as classNames, l as liveAnnounce } from '../theme-provider/index-chunk.esm.js';
import { g as getErrorMessage } from '../get-error-message/index-chunk.esm.js';
import { i as initGoogleMapsApi } from '../google-api/index-chunk.esm.js';
import { debounce } from 'throttle-debounce';
import { a as EGDSButton, E as EGDSButtonSize } from '../paging-button/index-chunk.esm.js';
import { E as EGDSFloatingFullWidthButton } from '../floating-full-width-button/index-chunk.esm.js';
import { i as EGDSLayoutFlex, j as EGDSLayoutFlexItem } from '../layout-flex-item/index-chunk.esm.js';
import { E as EGDSScrim, a as EGDSScrimType } from '../scrim/index-chunk.esm.js';
import { h as EGDSSheetTransition, e as EGDSSheet, f as EGDSSheetContent } from '../sheet-footer/index-chunk.esm.js';
import { b as EGDSToolbar } from '../toolbar-button/index-chunk.esm.js';
import styles from '../styles/uitk-egds-typeahead.css';
import { E as EGDSPrimaryButton } from '../primary-button/index-chunk.esm.js';
import { a as EGDSIcon, E as EGDSIconSize } from '../icon/index-chunk.esm.js';
import { a as EGDSActionListItem, E as EGDSActionList } from '../action-list-item-link/index-chunk.esm.js';
import { c as EGDSText } from '../text/index-chunk.esm.js';
import { b as EGDSSpacing } from '../spacing-hr/index-chunk.esm.js';
import { b as EGDSLineSkeleton } from '../skeleton-line/index-chunk.esm.js';
import { a as EGDSRemovablePill } from '../pill-container/index-chunk.esm.js';
import { E as EGDSTertiaryButton } from '../tertiary-button/index-chunk.esm.js';
import { i as isElementVisible } from '../is-element-visible/index-chunk.esm.js';
import { a as analyticsTracker } from '../analytics-tracker/index-chunk.esm.js';

class ThirdPartyAdapter {
    locale;
    category;
    sourceName;
    type;
    apiKey;
    constructor(locale) {
        this.locale = locale;
    }
    toEssResult(predictionDescription, placeId, index) {
        const displayName = predictionDescription;
        let primaryDisplayName = '';
        let secondaryDisplayName = '';
        const parts = (displayName || '').split(',');
        if (parts.length < 2) {
            primaryDisplayName = parts[0].trim();
        }
        else {
            primaryDisplayName = parts[0];
            secondaryDisplayName = parts
                .slice(1)
                .map((word) => word.trim())
                .join(', ')
                .trim();
        }
        return {
            category: this.category,
            coordinates: undefined,
            index,
            regionNames: {
                displayName,
                fullName: predictionDescription,
                lastSearchName: predictionDescription,
                primaryDisplayName,
                secondaryDisplayName,
            },
            sourceId: placeId,
            sourceName: this.sourceName,
            type: this.type,
        };
    }
}

const GOOGLEMAPS_CATEGORY = 'GOOGLEMAPS';
var PlacesServiceStatus;
(function (PlacesServiceStatus) {
    PlacesServiceStatus["INVALID_REQUEST"] = "INVALID_REQUEST";
    PlacesServiceStatus["OK"] = "OK";
    PlacesServiceStatus["OVER_QUERY_LIMIT"] = "OVER_QUERY_LIMIT";
    PlacesServiceStatus["REQUEST_DENIED"] = "REQUEST_DENIED";
    PlacesServiceStatus["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
    PlacesServiceStatus["ZERO_RESULTS"] = "ZERO_RESULTS";
})(PlacesServiceStatus || (PlacesServiceStatus = {}));
class GoogleAdapter extends ThirdPartyAdapter {
    google;
    googleAutoCompleteService;
    placesService;
    ready;
    static instances = {};
    constructor(locale, apiKey, version) {
        super(locale);
        this.category = GOOGLEMAPS_CATEGORY;
        this.sourceName = 'GPA';
        this.type = 'ADDRESS';
        const conf = { accessToken: apiKey, locale, version };
        const timeoutDuration = 10000;
        const timeoutPromise = new Promise((resolve, reject) => {
            setTimeout(() => {
                reject(new Error('Timeout: Map API initialization took too long'));
            }, timeoutDuration);
        });
        this.ready = Promise.race([initGoogleMapsApi(false, conf, false), timeoutPromise])
            .then(() => {
            this.google = window.google;
            this.initGooglePlacesService();
            return true;
        })
            .catch((err) => {
            console.error(err);
            return false;
        });
    }
    static getInstance(locale, apiKey, version) {
        const thisLocale = locale || 'en_US';
        const thisGoogleApiKey = apiKey ||
            (typeof window !== 'undefined' && typeof window.Cypress !== 'undefined'
                ? window.Cypress.env('GOOGLE_MAPS_API_KEY')
                : "AIzaSyABOFQNLMiTVN0AG3eWNmspVfVC8gNda68") ||
            '';
        if (!(thisLocale in this.instances)) {
            this.instances[thisLocale] = new GoogleAdapter(thisLocale, thisGoogleApiKey, version);
        }
        return this.instances[thisLocale];
    }
    isReady() {
        return this.ready;
    }
    initGooglePlacesService() {
        if (this.google && this.google.maps && this.google.maps.places) {
            const { AutocompleteService, PlacesService } = this.google.maps.places;
            this.googleAutoCompleteService = new AutocompleteService();
            this.placesService = new PlacesService(document.createElement('div'));
        }
    }
    fetchResults(essResponse) {
        const request = {
            input: essResponse.q,
            type: essResponse.rc === 'ADDRESS_AUTOCOMPLETE' ? ['geocode'] : undefined,
        };
        const toEssResponse = (results) => ({
            q: essResponse.q,
            rc: essResponse.rc,
            rid: essResponse.rid,
            sr: results,
        });
        return new Promise((resolve) => {
            if (this.googleAutoCompleteService) {
                this.googleAutoCompleteService.getPlacePredictions(request, (predictions, status) => {
                    if (status === PlacesServiceStatus.OK) {
                        const response = toEssResponse(this.handleResponse(predictions));
                        resolve(response);
                    }
                    else {
                        resolve(essResponse);
                    }
                });
            }
            else {
                resolve(essResponse);
            }
        });
    }
    fetchLocation(placeId) {
        return this.isReady().then((isReady) => new Promise((resolve, reject) => {
            if (!isReady === false) {
                try {
                    const request = {
                        placeId,
                        fields: ['geometry'],
                    };
                    this.placesService.getDetails(request, (place, status) => {
                        if (status === PlacesServiceStatus.OK) {
                            const loc = place?.geometry?.location;
                            if (loc)
                                resolve({
                                    lat: loc.lat().toFixed(5),
                                    lng: loc.lng().toFixed(5),
                                });
                            else
                                resolve(undefined);
                        }
                        else if (status === PlacesServiceStatus.ZERO_RESULTS)
                            resolve(undefined);
                        else
                            reject(new Error(`Failed to fetch location for placeId ${placeId}: ${status}`));
                    });
                }
                catch (error) {
                    reject(new Error(`Failed to fetch location for placeId ${placeId}: ${getErrorMessage(error)}`));
                }
            }
            else {
                reject(new Error(`Unable to fetch location for placeId ${placeId}: PlacesService is not initialized`));
            }
        }));
    }
    handleResponse(predictions) {
        const results = [];
        predictions.forEach((prediction, index) => {
            results.push(this.toEssResult(prediction.description, prediction.place_id, index));
        });
        return results;
    }
}

const appendQueryParam = (url, key, value) => {
    if (value === '' || value === null) {
        return url;
    }
    const punctuation = /\?/.test(url) ? '&' : '?';
    let query = encodeURIComponent(key);
    if (value !== undefined && value !== '') {
        query = query.concat('=', encodeURIComponent(value));
    }
    return url + punctuation + query;
};
const appendQueryObject = (url, queryObject) => {
    if (queryObject === null || queryObject === undefined) {
        return url;
    }
    let result = url;
    Object.keys(queryObject).forEach((key) => {
        const value = queryObject[key];
        if (value === undefined) {
            return;
        }
        result = appendQueryParam(result, key, value);
    });
    return result;
};

const mapBoxApiAccessToken = (typeof window !== 'undefined' && typeof window.Cypress !== 'undefined'
    ? window.Cypress.env('MAPBOX_API_ACCESS_TOKEN')
    : "pk.eyJ1IjoiZXhwZWRpYS1nZW8iLCJhIjoiY2s3NTc2aTExMDJwZDNmbnN1cDdvcmRzNSJ9.Grb_k2_kcl7NXEu6paSB1w") || '';
class MapboxAdapter extends ThirdPartyAdapter {
    language;
    static instances = {};
    constructor(locale, apiKey) {
        super(locale);
        [this.language, this.locale] = locale.split('_');
        this.category = 'MAPBOX';
        this.sourceName = 'mapbox';
        this.type = 'address';
        this.apiKey = apiKey || mapBoxApiAccessToken;
    }
    static getInstance(locale, apiKey) {
        const thisLocale = locale || 'en_US';
        const thisApiKey = apiKey || mapBoxApiAccessToken;
        if (!(thisLocale in this.instances)) {
            this.instances[thisLocale] = new MapboxAdapter(thisLocale, thisApiKey);
        }
        return this.instances[thisLocale];
    }
    getGeocodingUrl(queryStr, param) {
        const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${queryStr}.json?`;
        return appendQueryObject(endpoint, param);
    }
    fetchFromMapbox(queryStr) {
        const endpoint = this.getGeocodingUrl(queryStr, {
            autocomplete: true,
            language: this.language,
            types: this.type,
            fuzzyMatch: true,
            access_token: this.apiKey,
        });
        return fetch(endpoint).then((resp) => resp.json());
    }
    fetchResults(essResponse) {
        const toEssResponse = (results) => ({
            q: essResponse.q,
            rc: essResponse.rc,
            rid: essResponse.rid,
            sr: results,
        });
        return new Promise((resolve) => {
            this.fetchFromMapbox(essResponse.q)
                .then((respBody) => (respBody?.features === undefined ? [] : respBody.features))
                .then((predictions) => predictions.length === 0 ? resolve(essResponse) : resolve(toEssResponse(this.handleResponse(predictions))))
                .catch(() => {
                resolve(essResponse);
            });
        });
    }
    handleResponse(predictions) {
        const results = [];
        predictions.forEach((prediction, index) => {
            const placeId = prediction.id.split('.')[1];
            results.push(this.toEssResult(prediction.place_name, placeId, index));
        });
        return results;
    }
}

const thirdPartyAdapterRegister = {
    google: GoogleAdapter,
    mapbox: MapboxAdapter,
};
class ThirdPartyAdapterFactory {
    static getInstance(k, locale, apiKey, version) {
        return thirdPartyAdapterRegister[k].getInstance(locale, apiKey, version);
    }
    static filterThirdPartyAdapterFromEssConfig(configFeaturesList) {
        return configFeaturesList.filter((v) => v in thirdPartyAdapterRegister);
    }
}

var TypeaheadLocationType;
(function (TypeaheadLocationType) {
    TypeaheadLocationType["HOTEL"] = "HOTEL";
    TypeaheadLocationType["AIRPORT"] = "AIRPORT";
    TypeaheadLocationType["MULTICITY"] = "MULTICITY";
    TypeaheadLocationType["POI"] = "POI";
    TypeaheadLocationType["METROCODE"] = "METROCODE";
    TypeaheadLocationType["METROSTATION"] = "METROSTATION";
    TypeaheadLocationType["TRAINSTATION"] = "TRAINSTATION";
    TypeaheadLocationType["ADDRESS"] = "ADDRESS";
    TypeaheadLocationType["NEIGHBORHOOD"] = "NEIGHBORHOOD";
    TypeaheadLocationType["CURRENT_LOCATION"] = "CURRENT_LOCATION";
})(TypeaheadLocationType || (TypeaheadLocationType = {}));
var EssFeatures;
(function (EssFeatures) {
    EssFeatures["GOOGLE"] = "google";
    EssFeatures["CONSISTENT_DISPLAY"] = "consistent_display";
})(EssFeatures || (EssFeatures = {}));
class ESSAdapter {
    config;
    thirdPartyFallbackAdapter;
    l10n;
    viewSize = 0;
    ESSCategories;
    isGeoLocationAllowed = true;
    response;
    constructor(config) {
        this.config = config;
        this.config.getDevice = this.getDevice;
        if (config.typeaheadLogger) {
            this.initLoggers(config.typeaheadLogger);
        }
    }
    setTypeaheadCategories() {
        const { formatText } = this.l10n;
        this.ESSCategories = {
            ADDRESS: formatText('typeahead.ess.address'),
            AIRPORT: formatText('typeahead.ess.airport'),
            ATTRACTION: formatText('typeahead.ess.attraction'),
            CITY: formatText('typeahead.ess.city'),
            HOTEL: formatText('typeahead.ess.hotel'),
            METROCODE: formatText('typeahead.ess.city'),
            METROSTATION: formatText('typeahead.ess.metroStation'),
            MULTICITY: formatText('typeahead.ess.city'),
            MULTIREGION: formatText('typeahead.ess.city'),
            NEIGHBORHOOD: formatText('typeahead.ess.city'),
            POI: formatText('typeahead.ess.attraction'),
            TRAINSTATION: formatText('typeahead.ess.trainStation'),
        };
    }
    isHistoryDetailEnabled() {
        return this.config.historyDetail === true;
    }
    getTypeaheadCategories() {
        return this.ESSCategories;
    }
    addToESSCategories(newCategory) {
        this.ESSCategories = { ...this.ESSCategories, ...newCategory };
    }
    setL10n(l10n) {
        this.l10n = l10n;
    }
    initLoggers(logger) {
        const metadata = { ...this.config };
        delete metadata.typeaheadLogger; // Remove unwanted field which causes circular dependency
        logger.initLogger(metadata);
    }
    constructURL(query, makeAltDestCall) {
        let domain = this.config.domain ? this.config.domain : '';
        domain = domain.endsWith('/') ? domain : `${domain}/`;
        if (domain.endsWith('/')) {
            domain = domain.slice(0, -1);
        }
        const getBaseURL = () => {
            switch (this.config.domainId) {
                case 'AIRLINE':
                    return `${domain}/api/v2/suggest`;
                default:
                    return `${domain}/api/v4/typeahead`;
            }
        };
        const baseURL = getBaseURL();
        const URLParams = (params) => Object.entries(params)
            .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
            .join('&');
        if (typeof this.config.browser === 'undefined' || this.config.browser === null) {
            this.config.browser = this.getBrowser(window.navigator.userAgent);
        }
        if (typeof this.config.device === 'undefined' || this.config.device === null) {
            this.config.device = this.getDevice(window.navigator.userAgent);
        }
        if (this.config.domainId === 'AIRLINE') {
            const configV2 = {
                domainId: this.config.domainId,
                lob: this.config.lob,
                maxResults: this.config.maxResults,
                siteid: this.config.siteId,
                locale: this.config.locale,
            };
            return `${baseURL}?query=${query}&${URLParams(configV2)}`;
        }
        const config = {
            browser: this.config.browser,
            client: this.config.client,
            dest: this.config.dest,
            device: this.config.device,
            expuserid: this.config.expuserid ? this.config.expuserid : -1,
            features: this.config.features,
            format: 'json',
            guid: this.config.guid,
            listing: !!this.config.listing,
            lob: this.config.lob,
            locale: this.config.locale,
            maxresults: this.config.maxResults,
            personalize: this.config.personalize ? this.config.personalize : false,
            ...(this.config.regionId ? { rid: this.config.regionId } : {}),
            regiontype: this.config.regionType,
            ...(this.config.proximityMaxMiles && { proximityMaxMiles: this.config.proximityMaxMiles }),
            siteid: this.config.siteId,
            recentHistoryEnabled: !!this.config.recentHistoryEnabled,
            ...(this.config.packageType ? { pt: this.config.packageType } : {}),
            ...(this.config.latLong ? { latlong: this.config.latLong } : {}),
            ...(this.config.latlongSearchCenterPoint
                ? { latlongSearchCenterPoint: this.config.latlongSearchCenterPoint }
                : {}),
            ...(this.config.lastSearchRegionId ? { lsrid: this.config.lastSearchRegionId } : {}),
            ...(this.config.lastSearchTimestamp ? { lstime: this.config.lastSearchTimestamp } : {}),
            ...(this.config.lastSearchLatLong ? { lslatlong: this.config.lastSearchLatLong } : {}),
            ...(this.config.lastSearchElapseTime ? { lsetime: this.config.lastSearchElapseTime } : {}),
            ...(this.config.googleApiKey ? { googleApiKey: this.config.googleApiKey } : {}),
            ...(this.config.googleApiVersion ? { googleApiVersion: this.config.googleApiVersion } : {}),
            ...(this.config.alternate && !!makeAltDestCall ? { alternate: true } : {}),
            ...(this.config.debounceVariant ? { ab: this.config.debounceVariant } : {}),
            ...(this.config.trending ? { trending: this.config.trending } : {}),
            ...(this.config.alternateSecondVariant ? { alternateSecondVariant: this.config.alternateSecondVariant } : {}),
            ...(this.config.drivable ? { drivable: this.config.drivable } : {}),
            ...(this.config.fetchLocation ? { fetchLocation: this.config.fetchLocation } : {}),
        };
        const url = query === '' ? `${baseURL}/?${URLParams(config)}` : `${baseURL}/${query}?${URLParams(config)}`;
        return url;
    }
    fetchResponse(term, makeAltDestCall) {
        const personalize = this.config && this.config.personalize;
        if ((term === '' && personalize) || term !== '') {
            if (this.config.typeaheadLogger) {
                this.config.typeaheadLogger.logTypeaheadRequest(term);
            }
            const headers = {};
            if (this.config.xPageId) {
                headers['X-Page-Id'] = this.config.xPageId;
            }
            return fetch(this.constructURL(term, makeAltDestCall), {
                method: this.config.domainId === 'AIRLINE' ? 'POST' : 'GET',
                headers,
            }).then((response) => {
                if (this.config.typeaheadLogger) {
                    this.config.typeaheadLogger.logResponse(response);
                }
                return response.json();
            });
        }
        return Promise.resolve([]);
    }
    fetchResults(term, makeAltDestCall) {
        if (this.config.typeaheadLogger) {
            // This is because the control config is being overriden by variant and we don't want that
            this.config.typeaheadLogger.initLogger(this.config);
        }
        const searchTerm = makeAltDestCall ? '' : term;
        return this.fetchResponse(searchTerm, makeAltDestCall)
            .then((json) => this.handleEssResponse(json))
            .then((json) => this.logResponse(json))
            .then((json) => this.adaptResponseToTypeaheadResults(json, searchTerm, this.config.historyDetail))
            .then((json) => this.categorizeOnFocusResponse(json, makeAltDestCall));
    }
    fetchRecentSearchResults() {
        return this.fetchResults('');
    }
    fetchAlternativeResults(term) {
        return this.fetchResults(term, this.config.alternate);
    }
    fetchResultsCategorized(term) {
        if (this.config.typeaheadLogger) {
            // This is because the control config is being overriden by variant and we don't want that
            this.config.typeaheadLogger.initLogger(this.config);
        }
        return this.fetchResponse(term)
            .then((json) => this.handleEssResponse(json))
            .then((json) => this.logResponse(json))
            .then((json) => this.adaptResponseToTypeaheadResults(json, term, this.config.historyDetail))
            .then((json) => this.categorizeResponse(json));
    }
    fetchRecentSearchResultsCategorized() {
        return this.fetchResultsCategorized('');
    }
    categorizeResponse(typeaheadResults) {
        const resultsByCategory = new Map();
        const finalResults = [];
        let trueIndex = 0;
        if (typeaheadResults && typeaheadResults.length > 0) {
            // Creating categories and inserting to a map
            typeaheadResults.forEach((element) => {
                const categoryName = element?.data?.type ? this.ESSCategories[element.data.type] : '';
                if (!resultsByCategory[categoryName]) {
                    resultsByCategory[categoryName] = [];
                }
                resultsByCategory[categoryName].push(element);
            });
            for (const [heading, results] of Object.entries(resultsByCategory)) {
                // Pushing to an array of CategorizedResults
                // eslint-disable-next-line no-loop-func
                results.forEach((typeaheadResult) => {
                    // eslint-disable-next-line no-param-reassign
                    typeaheadResult.data.index = trueIndex;
                    // eslint-disable-next-line no-plusplus
                    trueIndex++;
                });
                const resultByCategory = {
                    heading,
                    typeaheadResults: results,
                };
                finalResults.push(resultByCategory);
            }
        }
        return finalResults;
    }
    onFocusCategories() {
        const { formatText } = this.l10n;
        return {
            CURRENTLOCATION: '',
            POPULAR: formatText('typeahead.ess.popular') || 'Explore popular destinations',
            DRIVABLE: formatText('typeahead.ess.nearby') || 'Nearby locations',
            ...(this.config.alternateSecondVariant
                ? {
                    USERHISTORY: formatText('typeahead.ess.history') || 'Recent searches',
                    ALTERNATE: formatText('typeahead.ess.alternate') || 'People also searched',
                }
                : {
                    ALTERNATE: formatText('typeahead.ess.alternate') || 'People also searched',
                    USERHISTORY: formatText('typeahead.ess.history') || 'Recent searches',
                }),
        };
    }
    categorizeOnFocusResponse(typeaheadResults, makeAltDestCall) {
        if (typeaheadResults && typeaheadResults.length > 0) {
            const element0 = typeaheadResults[0];
            if (element0.data.category === 'ALTERNATE' ||
                element0.data.category === 'CURRENTLOCATION' ||
                element0.data.category === 'POPULAR' ||
                (element0.data.category === 'DRIVABLE' && this.config.drivable) ||
                (element0.data.category === 'USERHISTORY' && this.config.alternate && makeAltDestCall)) {
                const resultsByCategory = new Map();
                const finalResults = [];
                const focusCategories = this.onFocusCategories();
                let trueIndex = 0;
                typeaheadResults.forEach((element) => {
                    if (element.data.category) {
                        const categoryName = element.data.category;
                        if (!resultsByCategory[categoryName]) {
                            resultsByCategory[categoryName] = [];
                        }
                        resultsByCategory[categoryName].push(element);
                    }
                });
                for (const [category, heading] of Object.entries(focusCategories)) {
                    const categorizedResult = resultsByCategory[category];
                    if (categorizedResult && categorizedResult.length > 0) {
                        // eslint-disable-next-line no-loop-func
                        categorizedResult.forEach((typeaheadResult) => {
                            // eslint-disable-next-line no-param-reassign
                            typeaheadResult.data.index = trueIndex;
                            // eslint-disable-next-line no-param-reassign
                            typeaheadResult.data.displayType = 'CATEGORIZED';
                            trueIndex += 1;
                        });
                        const resultByCategory = {
                            heading,
                            typeaheadResults: categorizedResult,
                        };
                        finalResults.push(resultByCategory);
                    }
                }
                if (makeAltDestCall || this.config.showCurrentLocation) {
                    // Log the typeahead response in the logger with the updated order
                    this.response.sr = finalResults.flatMap((categorizedResult) => categorizedResult.typeaheadResults.map((typeaheadResult) => typeaheadResult.data));
                    if (this.config.typeaheadLogger) {
                        this.config.typeaheadLogger.logTypeaheadResponse(this.response);
                    }
                }
                return finalResults;
            }
        }
        return typeaheadResults;
    }
    logSuggestionShown(term) {
        if (this.config.typeaheadLogger) {
            this.config.typeaheadLogger.logSuggestionShown(term);
        }
    }
    onItemSelection(data) {
        this.config.typeaheadLogger?.logSuggestionSelected(data);
        if (data?.type === TypeaheadLocationType.CURRENT_LOCATION) {
            return this.handleCurrentLocationSelection(data);
        }
        if (this.config.fetchLocation &&
            data?.type === TypeaheadLocationType.ADDRESS &&
            data.category === GOOGLEMAPS_CATEGORY &&
            data.sourceId &&
            typeof this.thirdPartyFallbackAdapter.fetchLocation === 'function') {
            return this.fillLocation(this.thirdPartyFallbackAdapter.fetchLocation.bind(this.thirdPartyFallbackAdapter), data.sourceId, data);
        }
        return Promise.resolve(data);
    }
    handleCurrentLocationSelection(data) {
        if (this.isGeoLocationAllowed) {
            return this.readCurrentLocation()
                .then(({ coords: { latitude, longitude } }) => ({
                ...data,
                location: { lat: latitude.toFixed(5), long: longitude.toFixed(5) },
            }))
                .catch((error) => {
                this.isGeoLocationAllowed = false;
                throw error;
            });
        }
        const message = this.l10n.formatText('typeahead.currentLocation.blocked.message');
        // eslint-disable-next-line no-alert
        window.alert(message);
        return Promise.reject(message);
    }
    fillLocation(fetchLocation, placeId, data) {
        return fetchLocation(placeId).then((location) => {
            if (location) {
                const { lat, lng } = location;
                return {
                    location: {
                        lat,
                        long: lng,
                    },
                    ...data,
                };
            }
            return data;
        });
    }
    /*
     * Note: Position is a global TypeScript interface representing the response to the navigator.geolocation call.
     * https://microsoft.github.io/PowerBI-JavaScript/interfaces/_node_modules_typedoc_node_modules_typescript_lib_lib_dom_d_.position.html
     */
    readCurrentLocation() {
        return new Promise((resolve, reject) => {
            window.navigator.geolocation.getCurrentPosition(resolve, reject);
        });
    }
    typeaheadDialogOpened(prefill) {
        if (this.config.typeaheadLogger) {
            const prefillText = prefill && prefill !== '' ? prefill : undefined;
            this.config.typeaheadLogger.logTypeaheadOpen(prefillText);
        }
    }
    logTypeaheadDismiss() {
        if (this.config.typeaheadLogger) {
            this.config.typeaheadLogger.logTypeaheadBlurEvent();
        }
    }
    handleEssResponse(json) {
        // eslint-disable-next-line no-param-reassign
        json.sr = json.sr || json.suggestions || [];
        if (!this.thirdPartyFallbackAdapter) {
            this.setThirdPartyAdapter();
        }
        if (this.thirdPartyFallbackAdapter && (json.rc === 'GOOGLE_AUTOCOMPLETE' || json.rc === 'ADDRESS_AUTOCOMPLETE')) {
            return this.thirdPartyFallbackAdapter.fetchResults(json);
        }
        return Promise.resolve(json);
    }
    setThirdPartyAdapter() {
        const fallbackAdapterRequested = ThirdPartyAdapterFactory.filterThirdPartyAdapterFromEssConfig(this.config.features.split('|'));
        let fallbackAdapter;
        if (fallbackAdapterRequested.length > 0) {
            fallbackAdapter = ThirdPartyAdapterFactory.getInstance(fallbackAdapterRequested[0], this.config.locale, this.config.googleApiKey, this.config.googleApiVersion);
            this.thirdPartyFallbackAdapter = fallbackAdapter;
        }
    }
    logResponse(results) {
        if (this.config.typeaheadLogger) {
            this.config.typeaheadLogger.logTypeaheadResponse(results);
        }
        this.response = results;
        return Promise.resolve(results);
    }
    adaptResponseToTypeaheadResults(results, term, historyDetail) {
        const getOneRoomLodgingString = (formatText, rooms, travelers, excludeRoomsLabelForRecentHistory) => excludeRoomsLabelForRecentHistory
            ? formatText('typeahead.recentHistory.travelers', travelers)
            : formatText('typeahead.recentHistory.roomsTravelers', rooms, travelers);
        const getListingTitle = (result) => result?.type === 'LISTING' && result.listingProps ? result.listingProps.headline : undefined;
        const getListingSubText = (result) => result?.type === 'LISTING' && result.listingProps
            ? `${result.listingProps.listingId} - ${result.listingProps.baseLocation}`
            : undefined;
        const getSubtext = (category, data) => {
            let subtextString;
            if (category === 'USERHISTORY' &&
                historyDetail &&
                typeof this.l10n !== 'undefined' &&
                data &&
                data.searchDetail &&
                data.searchDetail.lob &&
                !term) {
                let summaryString = '';
                const detail = data.searchDetail;
                const { formatDateString, formatText } = this.l10n;
                const rooms = detail.roomCount;
                const lineOfBusiness = detail.lob;
                const travelers = data.searchDetail.totalTravelerCount ? Number(data.searchDetail.totalTravelerCount) : null;
                if (data.searchDetail.startDate && data.searchDetail.endDate) {
                    // `TimezoneOffset` is used to prevent the days being off by one day because of local time conversion
                    const checkinDate = new Date(detail.startDate);
                    const checkinTimezoneOffset = checkinDate.getTimezoneOffset() * 60000;
                    const checkinString = formatDateString(new Date(checkinDate.getTime() + checkinTimezoneOffset), {
                        skeleton: 'MMMd',
                    });
                    const checkoutDate = new Date(detail.endDate);
                    const checkoutTimezoneOffset = checkoutDate.getTimezoneOffset() * 60000;
                    const checkoutString = formatDateString(new Date(checkoutDate.getTime() + checkoutTimezoneOffset), {
                        skeleton: 'MMMd',
                    });
                    // Dates are in milliseconds, so divide by ms/day to get number of days/nights
                    const nights = (Number(detail.endDate) - Number(detail.startDate)) / 86400000;
                    const nightsString = formatText('datePicker.nightSummary', nights);
                    subtextString = `${checkinString} - ${checkoutString}`;
                    if (rooms === 1 && (lineOfBusiness === 'Lodging' || lineOfBusiness === 'Package')) {
                        summaryString = getOneRoomLodgingString(formatText, rooms, travelers, this.config.excludeRoomsLabelForRecentHistory);
                        subtextString = `${checkinString} - ${checkoutString} | ${nightsString} | ${summaryString}`;
                    }
                    else if (lineOfBusiness === 'Lodging' || lineOfBusiness === 'Package') {
                        subtextString = `${checkinString} - ${checkoutString} | ${nightsString}`;
                    }
                }
                else if (!data.searchDetail.startDate && !data.searchDetail.endDate) {
                    if (rooms === 1 && (lineOfBusiness === 'Lodging' || lineOfBusiness === 'Package')) {
                        subtextString = getOneRoomLodgingString(formatText, rooms, travelers, this.config.excludeRoomsLabelForRecentHistory);
                    }
                }
            }
            return subtextString;
        };
        const parseDisplayText = (result) => {
            const subtext = getSubtext(result.category, result) || getListingSubText(result);
            if (this.config.features.indexOf(EssFeatures.CONSISTENT_DISPLAY) < 0) {
                const oneLineDisplayText = result.regionNames?.displayName || result.names?.displayName || getListingTitle(result);
                return { primaryText: oneLineDisplayText, subtext };
            }
            const firstLine = result.regionNames?.primaryDisplayName || result.names?.displayName || getListingTitle(result);
            if (subtext) {
                return {
                    primaryText: `<strong>${firstLine}</strong>`,
                    subtext,
                };
            }
            const secondLine = result.regionNames?.secondaryDisplayName || getListingSubText(result) || '';
            return {
                primaryText: `<strong>${firstLine}</strong>`,
                subtext: secondLine,
            };
        };
        // eslint-disable-next-line max-params
        const getIcon = (type, subType, isChild, isNearby, category) => {
            if (type === 'AIRLINE') {
                return '';
            }
            if (isChild && !isNearby) {
                return 'subdirectory_arrow_right';
            }
            if (category === 'USERHISTORY') {
                return 'history';
            }
            if (this.config.lob === 'FLIGHTS') {
                return 'lob_flights';
            }
            switch (type) {
                case 'HOTEL':
                    return 'lob_hotels';
                case 'AIRPORT':
                    return 'lob_flights';
                case 'ADDRESS':
                    if (subType === 'RENTALLOCATION') {
                        return 'lob_cars';
                    }
                    return 'place';
                case 'METROSTATION':
                case 'TRAINSTATION':
                    return 'train';
                default:
                    return 'place';
            }
        };
        const generateNewResults = (newResults, result, index) => {
            const isNearby = (result.hierarchyInfo &&
                result.hierarchyInfo.relation &&
                result.hierarchyInfo.relation.indexOf('nearby') > -1);
            const isChild = (result.hierarchyInfo &&
                result.hierarchyInfo.relation &&
                result.hierarchyInfo.relation.indexOf('child') > -1) || isNearby;
            const { primaryText, subtext } = parseDisplayText(result);
            const newResult = {
                data: {
                    ...(result.category ? { category: result.category } : {}),
                    ...(!term && result.searchDetail ? { searchDetail: result.searchDetail } : {}),
                    ...(result.userHistoryType ? { userHistoryType: result.userHistoryType } : {}),
                    ...(result.ids?.id ? { id: result.ids.id } : {}),
                    ...(result.coordinates
                        ? {
                            location: {
                                lat: result.coordinates.lat,
                                long: result.coordinates.long,
                            },
                        }
                        : {}),
                    ...(result.regionId ? { regionId: result.regionId } : result.gaiaId ? { regionId: result.gaiaId } : {}),
                    ...(result.cityId ? { cityId: result.cityId } : {}),
                    ...(result.regionNames ? { regionNames: result.regionNames } : {}),
                    ...(result.type ? { type: result.type } : {}),
                    ...(result.subtype ? { subtype: result.subtype } : {}),
                    ...(result.hotelId ? { selected: result.hotelId } : {}),
                    ...(result.hierarchyInfo ? { hierarchyInfo: result.hierarchyInfo } : {}),
                    ...(result.type === 'LISTING' && result.listingProps ? { listing: result.listingProps } : {}),
                    ...(result.sourceId ? { sourceId: result.sourceId } : {}),
                    ...(result.essId
                        ? {
                            essId: {
                                sourceId: result.essId.sourceId,
                                sourceName: result.essId.sourceName,
                            },
                        }
                        : {}),
                    ...{ dest: this.config.dest },
                    ...{ index: index + 1 },
                },
                icon: getIcon(result.type || result.domainId, result.subtype, isChild, isNearby, result.category),
                ...(isChild ? { isChild: true } : {}),
                name: primaryText,
                subtext,
                title: result.regionNames?.lastSearchName ||
                    result.regionNames?.shortName ||
                    result.regionNames?.fullName ||
                    result.listingProps?.listingId ||
                    result.ids?.id,
                value: result.regionNames?.lastSearchName ||
                    result.regionNames?.shortName ||
                    result.regionNames?.fullName ||
                    result.listingProps?.listingId ||
                    result.names?.displayName,
            };
            newResults.push(newResult);
            return newResults;
        };
        const baseResults = term === '' && this.shouldShowCurrentLocation() ? [this.getCurrentLocationItem()] : [];
        const adapt = (json) => json.sr?.reduce?.(generateNewResults, baseResults) || baseResults;
        return adapt(results);
    }
    /**
     * The Current Location feature is only shown on browsers which support it,
     * even when the showCurrentLocation config is set to true.
     */
    shouldShowCurrentLocation() {
        return Boolean(this.config.showCurrentLocation && 'geolocation' in navigator);
    }
    /**
     * The "Current location" result item is not returned by the back-end EGTA/ESS API, it is instead created
     * on the front-end and added to the beginning of the results when needed.
     */
    getCurrentLocationItem() {
        const label = this.isGeoLocationAllowed
            ? this.l10n.formatText('typeahead.currentLocation.available.item')
            : this.l10n.formatText('typeahead.currentLocation.blocked.item');
        return {
            data: {
                category: 'CURRENTLOCATION',
                hierarchyInfo: {
                    relation: ['current'],
                },
                index: -1,
                enabled: this.isGeoLocationAllowed,
                regionNames: {
                    fullName: 'CurrentLocation',
                },
                type: TypeaheadLocationType.CURRENT_LOCATION,
            },
            icon: 'my_location',
            name: label,
            title: label,
            value: label,
        };
    }
    getBrowser(ua) {
        let tem;
        let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        if (/trident/i.test(M[1])) {
            return 'IE';
        }
        if (M[1] === 'Chrome') {
            tem = ua.match(/\b(OPR|Edge?)\/(\d+)/);
            if (tem !== null) {
                return tem.slice(1, -1).join(' ').replace('OPR', 'Opera').replace('Edg ', 'Edge ');
            }
        }
        M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
        tem = ua.match(/version\/(\d+)/i);
        if (tem !== null) {
            M.splice(1, 1, tem[1]);
        }
        return M[0];
    }
    getDevice(ua) {
        if (/iPad|Transformer|ME173X|KOOF|Nook|IMM76D|Nexus 10|Nexus 7|Tablet/i.test(ua) ||
            /HP Slate|PG09410|PG41200|Mediapad|Kindle|Tab|Xoom|TegraNote|SCH-I800/i.test(ua)) {
            return 'Tablet';
        }
        if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
            return 'Mobile';
        }
        return 'Desktop';
    }
}

const getSizeOfTypeaheadResults = (typeaheadResults) => {
    /* eslint-disable-next-line default-case */
    switch (typeaheadResults.type) {
        case 'categorized':
            return typeaheadResults.items.reduce((sum, currentItem) => sum + currentItem.typeaheadResults.length, 0);
        case 'uncategorized':
            return typeaheadResults.items.length;
    }
};
const filterTypeaheadResults = (typeaheadResults, values) => {
    /* eslint-disable-next-line default-case */
    switch (typeaheadResults.type) {
        case 'categorized':
            return {
                ...typeaheadResults,
                items: typeaheadResults.items.map((category) => ({
                    ...category,
                    typeaheadResults: category.typeaheadResults.filter((item) => !values.includes(item.value)),
                })),
            };
        case 'uncategorized':
            return {
                ...typeaheadResults,
                items: typeaheadResults.items.filter((item) => !values.includes(item.value)),
            };
    }
};
const selectTypeaheadResultIndex = (typeaheadResults, index) => {
    /* eslint-disable-next-line default-case */
    switch (typeaheadResults.type) {
        case 'categorized': {
            return typeaheadResults.items.reduce((items, currentItem) => {
                items.push(...currentItem.typeaheadResults);
                return items;
            }, [])[index];
        }
        case 'uncategorized':
            return typeaheadResults.items[index];
    }
};

// To be converted into a UitkMark
const POWERED_BY_GOOGLE_LOGO = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAJAAAAASCAYAAAC0PldrAAAIHElEQVR4Ae3ZBXDbWB7H8efglpm' +
    'ZGW0HlhzJDpSZmZkZ3W3s2DpmZmbmKx0zM/NdoGhotxTf9x9LHY027paW85v5bBRQopn32weqasqzk5Jw7BE9nHijHo5/Rw/HfqaHYl/' +
    'keldZ8GJ7qBeqZ/6PNGlPUT5DeVKNqkyc18PJ6VDPtfEfnvUJz0dmpbwfmzMW6k5YFy96pBRTkCnL4MUDiy94oS0F+ZVZlD/5qxLLfNF' +
    'kz0D0fBtfKOkpCcffrFfG6vVQ4hDUc6ypQLdDFmMdMiWIcjywaOHEB8zynPEFa1pCOWnHLw2Bej5oKtDzqEC+cGyoWZ5YaTDeGer57r4' +
    'LRPLRCi40liy0QR4eVB5CG2QhU3KQneG+TM/qQhvk424L1Bwt73P22d9QoFD8bVB3KxVUWTX+gqW1mvvLtbr7Z7V+z6frNM/UlFIuKAD' +
    '83I2v5y69eiL3y9dPZP/s2tdyPn39ZN7UVEq5oCz8x+WvjC/SQrEv8/u/z/O9siSUKNfC8agWSbrRaIH+p3lH1Pg976jxe38gz8JzzLM' +
    '/g2QhpmIuIjCwB/0cg6HjGAxEsQQtIZmCtbBnLbY4BngZptkGaQmiMHAUhbCyEJMxCxGsh6QFltruOwIv7BmEfTAQwWwsvYMCTcMqGKa' +
    't6AJJCXYjD1ZcWIdZsEdK8K50gZLroez4+l4G8j1PURlfAJWaMye7Vvd+otbvTVVr7hvVmvc/ci0YyNdAidTHVPa1EzmfuH4iN0WBbnD' +
    '9H7lu8PXc10CJYDCVxanvffI8nAZvloQTNenZMXYt/YyJ5Y3tgar9nkCt5n2y4Tl073+5TprPEIYSknW2ARpkWosQOkFSiijK0BtuHME' +
    '2ZGMMDLSDpBMMU29bYaIYBxfW4wBGojcmw8BIWM8Vwjq40QMubMR+231TYGAYJN1QhVUYgEHYgMgdFCiCBeiLYdiJI2iO9og6St4XBgZ' +
    'nKpAWjm2AsmNZO+E8jdlnqzrdvS49aO4fndWLekDVlbmHMoB/MQewAorCrDML86P6E816QD35lbyhzER/SZcouwJKq0oskd9PSX9aVnm' +
    '5F1RJVbKQr13IVKCU251b7Xf/WwpcqxfMkVnngm9MW+sZ6gJeD24N1BHkwUouDmI28hDGBNjTDwZGIR9hFEOiYyu2YRIkY1GFhzAABvr' +
    'CnpVYbyvQAeTAyiBHKa2sNUnmY5/jvnwcvYMCbXDMmG1QBR2SJdhu+5l52NXYMsqA7EsXI/5OKBvEhnMKK7LwtePpgYy9HIpB+lbDIGm' +
    'FbihLTcAzs6FAuueDUJTkW0hdO5XrhrJc/1reTPk6BfsgZMY7iZS/KumFsmihxIFMBaoNeHxmiT/6H5+7Y41esJcl7K/pmdD9N64n4tZ' +
    'ALYQzs7EDvTMMmgtBW7GWYyUk26DDj/1wYYHt+z7bUhixMXDMVqDFznG5zX1HIdmDGbDnTvdAGpzZhGWO/2n6ojnCKIIzDNClwQ1LRmU' +
    '8ETCS3aEysU5r7FHmQckAyUD9sWJgPpSlNlA4SL7O0vJNqGtfz/6bFKX+SyofyvLk1/IGNcxAX8v9JpS8QpDfX/Ha+nwoC8vplIwFYq/' +
    'TUFbN+w9Zxqr93vpq3fOFWn/BJFlioYQ1UEvgzHxsRQ8Y6NdIgY6jDBI3wugGA53QxVa+IAogeQwR9EB3h27IVGwfqjLc1xWSnZgNZ5b' +
    'dQYECcGabrcgubMUCPIZjtztQWPsOZpjvPRq52A7KSatKTpV3QfxcXenL61tASUFk8M7q7gIoiywl6UF1fwBKCmLONAVQlhsn8+aYX/8' +
    'AVEll4pQ8h7x7grLIRj9TgWo0z8Ppsnqv1eqeyP98hX2hZClrrEDH0QpWWuIYJiMbQcx1TNWjYWCQbY8TwQbshJXdWI+obdPdEwbGwJ7' +
    'ipylQH2vZdN5nK9AMHEVzx1JUeQcF2uVY+rojimJYGY8q7MdEZIy8MGTwfmHuhf5JkTbJex/fyxJdtcpLDzNob5JNrbn/mQMl2ECvNpe' +
    'Kn1sDV6MVjGEz/U9zWSmFYuO82tzr/PzK6fy+UFdP5o6hNP9M742ySyFL1VJrDxSIXu4NJUunvAXPVCApCfufP5p7rg1y2pMZkeswX/u' +
    '+zIawF0jtwyOmvThiK5UXBhbDgwkIY7mjVKthoBRWymE0MngLbXsrN+bbS5WhQC4sRggVjvtGQ9IWQexGMR7BfjxxBwV6AptRgBIcxU7' +
    'kwkoODiOCtrhtZOZhkD52m3/KiFGs+VCAdYT3fsicAer5WIcU4DGghPzcta/nfKjhFPa13HrUybW5fBlQQk5hWmXi/fL3rNnuTk5hdX6' +
    'Pl79/wTwNXrJOYbIXOh9w98atgVqAx7EPx7AUHWDPSGy2la0MOY38zCp0gZWuWNXIbJONAPYhhK0YDSsTocGZbJRmuM9KJ6zAceyHD48' +
    '9zYyxCGMwGYdts24LOLMWi3DHkQ0sg/QqWU5KwvEf4HPMDHseDcc6QTnJUiH7EGajz8g7GPYiH2EWKoOyk/c9vAeax6zzGcryAz5+5Pr' +
    'J7DIoO37QxTufhRT0C+zLvst11FrC5JQG5f7IrJd5Pjzzx56Pzh4CJc76CnryHugVPMe3OcqfYTkLymkMCpk30RnTlG62jfQLIr5gKkf' +
    '+wVY29lAWWULTM2HsMah70VSgu888bIULL4hw2pqRPhEmfiMbadmbyWzE/utqSSj2nznBVB7UvWgq0N2lLSIYhxdOWL5k+Xzq/it2mdc' +
    'GAah7ZV00eQlgxvFRpNfJeyc+Bn2RK32h7sf/AesqcHB02e65AAAAAElFTkSuQmCC';

const POWERED_BY_MAPBOX_LOGO = 'M9.07917722,0.0888888889 C4.06575949,0.0888888889 0,4.52222222 0,9.98888889' +
    ' C0,15.4555556 4.06575949,19.8888889 9.07917722,19.8888889 C14.0925949,19.8888889 18.1583544,15.4555556 18.1583' +
    '544,9.98888889 C18.1583544,4.52222222 14.0925949,0.0888888889 9.07917722,0.0888888889 Z M55.7284177,2.27777778 ' +
    'C55.6061392,2.27777778 55.5042405,2.38888889 55.5042405,2.52222222 L55.5042405,13.9888889 C55.5042405,14.122222' +
    '2 55.6061392,14.2333333 55.7284177,14.2333333 L57.0938608,14.2333333 C57.2161392,14.2333333 57.318038,14.122222' +
    '2 57.318038,13.9888889 L57.318038,13.2 C58.0211392,14 58.9891772,14.4555556 59.9979747,14.4555556 C62.1276582,1' +
    '4.4555556 63.8599367,12.4555556 63.8599367,9.97777778 C63.8599367,7.5 62.1276582,5.51111111 59.9979747,5.511111' +
    '11 C58.9789873,5.51111111 58.0109494,5.96666667 57.318038,6.76666667 L57.318038,2.52222222 C57.318038,2.3888888' +
    '9 57.2161392,2.27777778 57.0938608,2.27777778 L55.7284177,2.27777778 Z M10.0166456,3.93333333 C11.1782911,3.966' +
    '66667 12.3501266,4.46666667 13.2468354,5.45555556 C15.050443,7.42222222 15.1115823,10.5333333 13.3894937,12.422' +
    '2222 C10.2815823,15.8111111 4.74848101,14.7222222 4.74848101,14.7222222 C4.74848101,14.7222222 3.74987342,8.688' +
    '88889 6.85778481,5.3 C7.72392405,4.36666667 8.86518987,3.91111111 10.0166456,3.93333333 Z M26.3510127,5.5111111' +
    '1 C25.515443,5.51111111 24.7308228,5.95555556 24.231519,6.68888889 L24.231519,5.97777778 C24.231519,5.84444444 ' +
    '24.1296203,5.73333333 24.0073418,5.73333333 L22.6418987,5.73333333 C22.5196203,5.73333333 22.4177215,5.84444444' +
    ' 22.4177215,5.97777778 L22.4177215,14 C22.4177215,14.1333333 22.5196203,14.2444444 22.6418987,14.2444444 L24.00' +
    '73418,14.2444444 C24.1296203,14.2444444 24.231519,14.1333333 24.231519,14 L24.231519,9.2 C24.2824684,8.12222222' +
    ' 24.9651899,7.27777778 25.8007595,7.27777778 C26.6668987,7.27777778 27.3903797,8.06666667 27.3903797,9.11111111' +
    ' L27.3903797,14 C27.3903797,14.1333333 27.4922785,14.2444444 27.614557,14.2444444 L28.9901899,14.2444444 C29.11' +
    '24684,14.2444444 29.2143671,14.1333333 29.2143671,14 L29.2041772,9.01111111 C29.3264557,8.03333333 29.9786076,7' +
    '.27777778 30.7632278,7.27777778 C31.6293671,7.27777778 32.3528481,8.06666667 32.3528481,9.11111111 L32.3528481,' +
    '14 C32.3528481,14.1333333 32.4547468,14.2444444 32.5770253,14.2444444 L33.9526582,14.2444444 C34.0749367,14.244' +
    '4444 34.1768354,14.1333333 34.1768354,14 L34.1666456,8.48888889 C34.1972152,6.84444444 32.9132911,5.51111111 31' +
    '.3236709,5.51111111 C30.3046835,5.52222222 29.3672152,6.16666667 28.9290506,7.17777778 C28.419557,6.14444444 27' +
    '.4311392,5.5 26.3510127,5.51111111 L26.3510127,5.51111111 Z M39.3838608,5.51111111 C37.2541772,5.51111111 35.52' +
    '18987,7.51111111 35.5218987,9.98888889 C35.5218987,12.4666667 37.2541772,14.4666667 39.3838608,14.4666667 C40.4' +
    '028481,14.4666667 41.3708861,14.0111111 42.0637975,13.2111111 L42.0637975,14 C42.0637975,14.1333333 42.1656962,' +
    '14.2444444 42.2879747,14.2444444 L43.6534177,14.2444444 C43.7756962,14.2444444 43.8775949,14.1333333 43.8775949' +
    ',14 L43.8775949,5.97777778 C43.8877848,5.84444444 43.7858861,5.73333333 43.6534177,5.73333333 L42.2879747,5.733' +
    '33333 C42.1656962,5.73333333 42.0637975,5.84444444 42.0637975,5.97777778 L42.0637975,6.76666667 C41.3606962,5.9' +
    '6666667 40.3926582,5.51111111 39.3838608,5.51111111 Z M50.1953165,5.51111111 C49.1763291,5.51111111 48.2082911,' +
    '5.96666667 47.5153797,6.76666667 L47.5153797,5.97777778 C47.5153797,5.84444444 47.413481,5.73333333 47.2912025,' +
    '5.73333333 L45.9257595,5.73333333 C45.803481,5.73333333 45.7015823,5.84444444 45.7015823,5.97777778 L45.7015823' +
    ',17.4444444 C45.7015823,17.5777778 45.803481,17.6888889 45.9257595,17.6888889 L47.2912025,17.6888889 C47.413481' +
    ',17.6888889 47.5153797,17.5777778 47.5153797,17.4444444 L47.5153797,13.2 C48.218481,14 49.186519,14.4555556 50.' +
    '1953165,14.4555556 C52.325,14.4555556 54.0572785,12.4555556 54.0572785,9.97777778 C54.0572785,7.5 52.325,5.5111' +
    '1111 50.1953165,5.51111111 Z M69.0975316,5.51111111 C66.7844304,5.51111111 64.9196835,7.51111111 64.9196835,9.9' +
    '8888889 C64.9196835,12.4666667 66.7946203,14.4666667 69.0975316,14.4666667 C71.400443,14.4666667 73.2753797,12.' +
    '4666667 73.2753797,9.98888889 C73.2753797,7.51111111 71.4106329,5.51111111 69.0975316,5.51111111 Z M73.7237342,' +
    '5.73333333 C73.6116456,5.73333333 73.5199367,5.83333333 73.5199367,5.95555556 C73.5199367,6 73.5301266,6.044444' +
    '44 73.5505063,6.07777778 L75.8941772,9.96666667 L73.5199367,13.9 C73.4587975,14 73.4791772,14.1444444 73.581075' +
    '9,14.2111111 C73.6116456,14.2333333 73.6524051,14.2444444 73.6931646,14.2444444 L75.2725949,14.2444444 C75.3948' +
    '734,14.2444444 75.506962,14.1777778 75.5681013,14.0666667 L76.9743038,11.5 L78.3805063,14.0666667 C78.4416456,1' +
    '4.1777778 78.5537342,14.2444444 78.6760127,14.2444444 L80.255443,14.2444444 C80.3675316,14.2444444 80.4592405,1' +
    '4.1444444 80.4592405,14.0222222 C80.4592405,13.9777778 80.4490506,13.9444444 80.4286709,13.9 L78.0544304,9.9666' +
    '6667 L80.3981013,6.07777778 C80.4592405,5.97777778 80.4388608,5.83333333 80.336962,5.76666667 C80.3063924,5.744' +
    '44444 80.2656329,5.73333333 80.2248734,5.73333333 L78.645443,5.73333333 C78.5231646,5.73333333 78.4110759,5.8 7' +
    '8.3499367,5.91111111 L76.9743038,8.43333333 L75.5986709,5.91111111 C75.5375316,5.8 75.425443,5.73333333 75.3031' +
    '646,5.73333333 L73.7237342,5.73333333 Z M10.1185443,5.88888889 L9.23202532,7.88888889 L7.40803797,8.85555556 L9' +
    '.23202532,9.82222222 L10.1185443,11.8222222 L11.0152532,9.82222222 L12.8392405,8.85555556 L11.0152532,7.8888888' +
    '9 L10.1185443,5.88888889 Z M39.6997468,7.3 C40.9938608,7.3 42.0434177,8.48888889 42.0637975,9.95555556 L42.0637' +
    '975,10.0222222 C42.0536076,11.4888889 40.9938608,12.6777778 39.6997468,12.6777778 C38.395443,12.6777778 37.3356' +
    '962,11.4777778 37.3356962,9.98888889 C37.3356962,8.5 38.395443,7.3 39.6997468,7.3 L39.6997468,7.3 Z M49.8692405' +
    ',7.3 C51.1735443,7.3 52.2332911,8.5 52.2332911,9.98888889 C52.2332911,11.4777778 51.1735443,12.6777778 49.86924' +
    '05,12.6777778 C48.5751266,12.6777778 47.5255696,11.4888889 47.5051899,10.0222222 L47.5051899,9.95555556 C47.525' +
    '5696,8.48888889 48.5751266,7.3 49.8692405,7.3 L49.8692405,7.3 Z M59.6820886,7.3 C60.9863924,7.3 62.0461392,8.5 ' +
    '62.0461392,9.98888889 C62.0461392,11.4777778 60.9863924,12.6777778 59.6820886,12.6777778 C58.3879747,12.6777778' +
    ' 57.3384177,11.4888889 57.318038,10.0222222 L57.318038,9.95555556 C57.3384177,8.48888889 58.3879747,7.3 59.6820' +
    '886,7.3 L59.6820886,7.3 Z M69.0771519,7.3 C70.3814557,7.3 71.4412025,8.5 71.4412025,9.98888889 C71.4412025,11.4' +
    '777778 70.3814557,12.6777778 69.0771519,12.6777778 C67.7728481,12.6777778 66.7131013,11.4777778 66.7131013,9.98' +
    '888889 C66.7131013,8.5 67.7728481,7.3 69.0771519,7.3 Z';

const getClassNameList$7 = classNames.bind(styles);
const EGDSTypeaheadAttribution = (props) => {
    const { results } = props;
    let category;
    const extractCategoryFromTypeaheadResult = (taResult) => (taResult ? taResult.data?.category : '');
    const findAttributedCategory = (taResults) => taResults.find((res) => res.data && res.data.category);
    const getCategoryForUnCategorizedResults = (typeaheadResults) => extractCategoryFromTypeaheadResult(findAttributedCategory(typeaheadResults));
    const getCategoryForCategorizedResults = (typeaheadResults) => {
        const typeaheadDataWithCategory = typeaheadResults.find((item) => findAttributedCategory(item.typeaheadResults));
        if (typeof typeaheadDataWithCategory === 'undefined' || !typeaheadDataWithCategory.typeaheadResults)
            return '';
        return extractCategoryFromTypeaheadResult(typeaheadDataWithCategory.typeaheadResults[0]);
    };
    const filterAttributionResults = (typeaheadResults) => typeaheadResults.some((result) => {
        const key = 'category';
        return result.data && result.data[key] && result.data[key] === category;
    });
    const showLogoForCategorizedResults = (typeaheadResults) => typeaheadResults.some((item) => filterAttributionResults(item.typeaheadResults));
    const showLogoForUnCategorizedResults = (typeaheadResults) => filterAttributionResults(typeaheadResults);
    const showLogo = (typeaheadResults) => {
        // eslint-disable-next-line default-case
        switch (typeaheadResults.type) {
            case 'categorized':
                category = getCategoryForCategorizedResults(typeaheadResults.items);
                return showLogoForCategorizedResults(typeaheadResults.items);
            case 'uncategorized':
                category = getCategoryForUnCategorizedResults(typeaheadResults.items);
                return showLogoForUnCategorizedResults(typeaheadResults.items);
        }
    };
    if (!showLogo(results)) {
        return null;
    }
    switch (category) {
        case GOOGLEMAPS_CATEGORY:
            return (jsx("div", { className: getClassNameList$7('uitk-typeahead-google-logo'), children: jsx("img", { src: POWERED_BY_GOOGLE_LOGO, alt: "Powered by Google Logo" }) }));
        case 'MAPBOX':
            return (jsxs("div", { className: getClassNameList$7('uitk-typeahead-google-logo'), children: ["powered by", ' ', jsx("svg", { viewBox: "0 0 81 20", width: "81px", height: "20px", children: jsx("path", { d: POWERED_BY_MAPBOX_LOGO }) })] }));
        default:
            return null;
    }
};

const getClassNameList$6 = classNames.bind(styles);
class EGDSTypeaheadResultItem extends React.Component {
    static displayName = 'EGDSTypeaheadResultItem';
    constructor(props) {
        super(props);
        this.handleClick = this.handleClick.bind(this);
    }
    handleClick() {
        const { data, onResultItemSelect, value } = this.props;
        onResultItemSelect(value, data);
    }
    // A safer way to get the sanitized name.
    // Doing so with regex string replacement throws a code QL exception
    getSanitizedName = (name) => {
        const Elem = document.createElement('div');
        Elem.innerHTML = name;
        return Elem.textContent;
    };
    render() {
        const { activeSuggestion, className, icon, index, name, stid, sanitized, subtext, contentRef, historyDetail, } = this.props;
        const cssClassNames = getClassNameList$6(className, subtext ? 'uitk-typeahead-result-item has-subtext' : 'uitk-typeahead-result-item', activeSuggestion === true && 'uitk-typeahead-result-item-hover');
        if (activeSuggestion === true) {
            const sanitizedName = this.getSanitizedName(name);
            liveAnnounce(`${sanitizedName}`, undefined, undefined, contentRef);
        }
        let ariaLabel;
        let iconContent;
        let contentFirstLine;
        let contentSecondLine;
        const searchDetailID = `full-search-detail-${historyDetail || false}`;
        if (sanitized) {
            contentFirstLine = jsx("span", { children: name });
        }
        else {
            contentFirstLine = (jsx("div", { className: getClassNameList$6('truncate'), children: jsx("span", { dangerouslySetInnerHTML: { __html: name } }) }));
            if (subtext) {
                contentSecondLine = (jsx("div", { "data-stid": searchDetailID, className: getClassNameList$6('is-subText', 'truncate'), dangerouslySetInnerHTML: { __html: subtext } }));
            }
        }
        if (icon) {
            iconContent = jsx(EGDSIcon, { name: icon, size: EGDSIconSize.SMALL });
        }
        if (subtext) {
            ariaLabel = `${name} ${subtext}`.replace(/(<([^>]+)>)/gi, '');
        }
        else {
            ariaLabel = name?.replace(/(<([^>]+)>)/gi, '');
        }
        return (jsxs(EGDSActionListItem, { spacing: "relaxed", className: cssClassNames, "data-stid": stid && `${stid}-result-item`, "data-index": index, children: [jsx(EGDSButton, { "data-stid": stid && `${stid}-result-item-button`, onClick: this.handleClick, className: getClassNameList$6(subtext && 'has-subtext', `${stid}-result-item-button`, 'result-item-button'), "aria-label": ariaLabel, type: "button", isFullWidth: true }), jsxs(EGDSLayoutFlex, { children: [jsx(EGDSLayoutFlexItem, { alignSelf: "center", children: iconContent }), jsx(EGDSLayoutFlexItem, { children: jsxs("div", { className: getClassNameList$6('uitk-typeahead-button-label', 'truncate'), children: [contentFirstLine, contentSecondLine] }) })] })] }));
    }
}

const getClassNameList$5 = classNames.bind(styles);
const EGDSTypeaheadSelections = (props) => {
    const { headingText, hideHeading = false, typeaheadLabel, stid = '', values = [], 
    /* eslint-disable @typescript-eslint/no-empty-function */
    onItemRemove = () => { }, 
    /* eslint-disable @typescript-eslint/no-empty-function */
    onClear = () => { }, clearAllText, className, } = props;
    const l10n = useLocalization();
    const clearAll = clearAllText ?? l10n.formatText('typeahead.mulselect.clearAll');
    const heading = headingText ?? l10n.formatText('typeahead.multiselect.selectedItemHeading');
    const handleRemove = (value, index) => {
        liveAnnounce(l10n.formatText('typeahead.removeSelection', value));
        onItemRemove(index);
    };
    const handleClear = () => {
        liveAnnounce(l10n.formatText('typeahead.clearAllSelections', typeaheadLabel));
        onClear();
    };
    return (jsxs("div", { className: getClassNameList$5('uitk-typeahead-selections', className), children: [!hideHeading && heading && values.length > 0 ? (jsx("div", { className: getClassNameList$5('uitk-typeahead-result-title-label', 'truncate'), children: heading })) : null, jsxs("div", { className: getClassNameList$5('uitk-typeahead-pills'), "data-stid": stid, children: [(Array.isArray(values) ? values : [values]).map((value, index) => (jsx(EGDSRemovablePill, { onClick: () => handleRemove(value, index), id: `${stid}-pill-${index}`, className: getClassNameList$5('uitk-typeahead-pill-removable'), children: value }, `${stid}-pill-${index}`))), values.length ? (jsx(EGDSTertiaryButton, { onClick: handleClear, size: EGDSButtonSize.SMALL, children: clearAll })) : null] })] }));
};

const getClassNameList$4 = classNames.bind(styles);
const getResultItemClassName = (result) => {
    const isCurrentLocation = result && result.data && result.data.type === TypeaheadLocationType.CURRENT_LOCATION;
    const resultItemClassNames = getClassNameList$4({
        'is-child': result.data && result.isChild,
        'uitk-typeahead-current-location': isCurrentLocation && result.data.enabled,
        disabled: isCurrentLocation && !result.data.enabled,
    });
    return resultItemClassNames;
};
const EGDSTypeaheadResults = (props) => {
    const { activeSuggestion, allowExactMatch, listTitle, typeaheadLabel, onResultItemSelect, results, searchTerm, stid, emptyResultPlaceholder, isMulti, onClearAll, onItemRemove, clearAllText, selectedItemHeading, value, domRef, contentRef, historyDetail, } = props;
    const l10n = useLocalization();
    const filteredResults = isMulti ? filterTypeaheadResults(results, value) : results;
    const resultsLen = getSizeOfTypeaheadResults(filteredResults);
    const cssClassNames = isMulti ? 'uitk-typeahead-multi-selection-results' : 'uitk-typeahead-results';
    const renderSelections = () => isMulti && value.length > 0 ? (jsx(EGDSTypeaheadSelections, { typeaheadLabel: typeaheadLabel, values: value, stid: stid, clearAllText: clearAllText, onClear: onClearAll, onItemRemove: onItemRemove, headingText: selectedItemHeading, className: getClassNameList$4('uitk-typeahead-results-selections') })) : null;
    if (!results || results.items.length === 0) {
        return emptyResultPlaceholder ? (jsx(EGDSSpacing, { padding: { blockstart: 'three' }, children: jsxs("div", { children: [renderSelections(), jsx(EGDSLayoutFlex, { justifyContent: "center", alignContent: "center", className: getClassNameList$4('uitk-typeahead-empty-state'), children: jsxs(EGDSText, { align: "center", children: [jsx(EGDSSpacing, { margin: { blockstart: 'six' }, children: jsx(EGDSIcon, { name: "search", size: EGDSIconSize.XLARGE }) }), jsx(EGDSSpacing, { margin: { blockend: 'three' }, children: jsx(EGDSText, { size: 300, children: emptyResultPlaceholder }) })] }) })] }) })) : (jsx(EGDSSpacing, { padding: { blockstart: 'three' }, children: jsx("div", { children: renderSelections() }) }));
    }
    const handleResultItemSelect = (itemValue, data) => onResultItemSelect(itemValue, data, filteredResults);
    const isTypeaheadResultsLoading = (loadingResults) => loadingResults[0]?.disabled;
    const isCategorizedResultsLoading = (categories) => categories[0]?.typeaheadResults[0]?.disabled;
    const isResultsLoading = (resultsDropdown) => {
        switch (resultsDropdown.type) {
            case 'categorized':
                return isCategorizedResultsLoading(resultsDropdown.items);
            case 'uncategorized':
                return isTypeaheadResultsLoading(resultsDropdown.items);
        }
    };
    const renderSkeletons = () => (jsxs("div", { className: getClassNameList$4('uitk-typeahead-results-skeleton-container'), children: [jsx(EGDSLineSkeleton, { className: getClassNameList$4('uitk-typeahead-results-skeleton', 'uitk-typeahead-results-skeleton-first') }), jsx(EGDSLineSkeleton, { className: getClassNameList$4('uitk-typeahead-results-skeleton', 'uitk-typeahead-results-skeleton-second') }), jsx(EGDSLineSkeleton, { className: getClassNameList$4('uitk-typeahead-results-skeleton') })] }));
    const showUnCategorizedResults = (typeaheadResults) => typeaheadResults &&
        typeaheadResults.map((result, index) => (jsxs(React.Fragment, { children: [listTitle && (jsx(EGDSActionListItem, { spacing: "relaxed", className: getClassNameList$4('uitk-typeahead-result-title'), children: jsx("div", { className: getClassNameList$4('uitk-typeahead-result-title-label', 'truncate'), children: result.title }) })), jsx(EGDSTypeaheadResultItem, { className: getResultItemClassName(result), data: result.data, index: index, icon: result.icon, name: result.name, onResultItemSelect: handleResultItemSelect, stid: stid, value: result.value, subtext: result.subtext, activeSuggestion: activeSuggestion !== -1 && activeSuggestion === index, contentRef: contentRef, historyDetail: historyDetail }, index)] }, index)));
    const showCategorizedResults = (categorizedResults) => categorizedResults &&
        categorizedResults.map((category) => (jsxs(React.Fragment, { children: [category.heading && (jsx(EGDSActionListItem, { spacing: "relaxed", className: getClassNameList$4('uitk-typeahead-result-title'), children: jsx("div", { className: getClassNameList$4('uitk-typeahead-result-title-label', 'truncate'), children: category.heading }) })), category.typeaheadResults &&
                    category.typeaheadResults.map((item, index) => (jsx(EGDSTypeaheadResultItem, { className: getResultItemClassName(item), data: item.data, index: index, icon: item.icon, name: item.name, onResultItemSelect: handleResultItemSelect, stid: stid, value: item.value, subtext: item.subtext, activeSuggestion: activeSuggestion !== -1 && activeSuggestion === item.data?.index, historyDetail: historyDetail }, index)))] }, category.heading)));
    const showResults = (resultsDropdown) => {
        switch (resultsDropdown.type) {
            case 'categorized':
                return isCategorizedResultsLoading(resultsDropdown.items)
                    ? renderSkeletons()
                    : showCategorizedResults(resultsDropdown.items);
            case 'uncategorized':
                return isTypeaheadResultsLoading(resultsDropdown.items)
                    ? renderSkeletons()
                    : showUnCategorizedResults(resultsDropdown.items);
        }
    };
    return (jsx(EGDSSpacing, { padding: { blockstart: 'three' }, children: jsxs("div", { className: getClassNameList$4(cssClassNames), ref: domRef, children: [renderSelections(), jsxs(EGDSActionList, { className: getClassNameList$4('no-bullet'), "data-stid": stid && `${stid}-results`, children: [showResults(filteredResults), allowExactMatch && searchTerm && !isResultsLoading(filteredResults) && (jsx(EGDSTypeaheadResultItem, { icon: "search", name: l10n.formatText('typeahead.searchFor', [searchTerm]), onResultItemSelect: handleResultItemSelect, index: resultsLen, stid: stid, value: searchTerm, sanitized: true, activeSuggestion: !!(activeSuggestion !== -1 && activeSuggestion === resultsLen), historyDetail: historyDetail }))] }), jsx(EGDSTypeaheadAttribution, { results: filteredResults })] }) }));
};

const getClassNameList$3 = classNames.bind(styles);
var TypeaheadNavKeys$1;
(function (TypeaheadNavKeys) {
    TypeaheadNavKeys["UP"] = "UP";
    TypeaheadNavKeys["DOWN"] = "DOWN";
})(TypeaheadNavKeys$1 || (TypeaheadNavKeys$1 = {}));
class TypeaheadDialog extends React.Component {
    static placeholder = [
        { disabled: true, icon: 'brightness_1', name: ' ', value: ' ', title: ' ' },
    ];
    toolbarInputRef = React.createRef();
    iphoneSafariUARegex = /iphone.*safari|safari.*iphone/gi;
    uid = Math.random().toString(36).substring(7);
    typeaheadDialogLiveAnnounceId = `uitk-typeahead-dialog-live-announce-${this.uid}`;
    lastSearchTerm;
    typingTimeout = 0;
    newAttr = -1;
    _isMounted = false;
    contentRef;
    updateResultsDebounced;
    constructor(props) {
        super(props);
        this.state = {
            activeSuggestion: -1,
            categorizedResults: [],
            // eslint-disable-next-line react/no-unused-state
            data: {},
            results: [],
            searchTerm: props.term,
            showResults: true,
            // eslint-disable-next-line react/no-unused-state
            startSearchTerm: props.startSearchTerm,
            isCurrentLocation: false,
        };
        this.announceResults = this.announceResults.bind(this);
        this.focusInput = this.focusInput.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleAnnounceResults = this.handleAnnounceResults.bind(this);
        this.handleResultItemSelect = this.handleResultItemSelect.bind(this);
        this.onDismiss = this.onDismiss.bind(this);
        this.openDialog = this.openDialog.bind(this);
        this.updateResults = this.updateResults.bind(this);
        this.updateResultsDebounced =
            props.debounceRate && props.debounceRate > 0
                ? debounce(props.debounceRate, this.updateResults)
                : this.updateResults;
        this.updateInput = this.updateInput.bind(this);
        this.selectInput = this.selectInput.bind(this);
        this.resetActiveSuggestion = this.resetActiveSuggestion.bind(this);
        this.removeDialogLiveAnnounce = this.removeDialogLiveAnnounce.bind(this);
        this.contentRef = React.createRef();
    }
    componentDidMount() {
        this._isMounted = true;
    }
    componentDidUpdate(prevProps, prevState) {
        const { term } = this.props;
        if (term !== prevProps.term) {
            this.setState({
                searchTerm: term,
            });
        }
        const { searchTerm, showResults } = this.state;
        if (prevState.searchTerm !== searchTerm) {
            if (showResults) {
                this.setState({
                    results: TypeaheadDialog.placeholder,
                });
                this.updateResultsDebounced(searchTerm);
            }
        }
    }
    componentWillUnmount() {
        const { adapter } = this.props;
        if (adapter && typeof adapter.logTypeaheadDismiss === 'function') {
            adapter.logTypeaheadDismiss();
        }
        this._isMounted = false;
    }
    handleChange(event) {
        const { updateCachedSearchTerm, onInputChange } = this.props;
        this.setState({ searchTerm: event.target.value, showResults: true, isCurrentLocation: false }, this.focusInput);
        // To reset activeSuggestion when value is cleared
        if (event.target.value === '') {
            this.resetActiveSuggestion();
        }
        onInputChange && onInputChange(event);
        updateCachedSearchTerm && updateCachedSearchTerm(event);
    }
    handleResultItemSelect(term, data, results) {
        const { isMulti, adapter, l10n, label, onResultItemSelect } = this.props;
        const { searchTerm } = this.state;
        return adapter
            .onItemSelection?.(data)
            .then((result) => {
            if (isMulti) {
                this.setState({
                    activeSuggestion: -1,
                });
            }
            else {
                this.setState({
                    activeSuggestion: -1,
                    searchTerm: term,
                    showResults: false,
                    isCurrentLocation: data?.type === TypeaheadLocationType.CURRENT_LOCATION,
                });
            }
            onResultItemSelect(term, result);
            // Results is the results prior to us triggering onResulItemSelect so we subtract 1
            // This is technically not correct because the typeahead is controlled and users could decide to not select the value (or do any other logic) in onChange even if that would not make sense.  Ideally liveAnnnounce logic would trigger based on state change and not on events
            const searchLen = results ? getSizeOfTypeaheadResults(results) - 1 : 0;
            if (isMulti) {
                liveAnnounce(`${l10n.formatText(`typeahead.announceResults.itemSelected`, term)}
             ${l10n.formatText('typeahead.announceResults.showingResults', label, searchLen, searchTerm)}`, undefined, undefined, this.contentRef, this.typeaheadDialogLiveAnnounceId);
            }
            if (!isMulti) {
                setTimeout(() => {
                    liveAnnounce(`${l10n.formatText(`typeahead.announceResults.itemSelected`, term)}
             ${l10n.formatText('typeahead.announceResults.menuClosed')}`);
                }, 100);
                this.onDismiss();
            }
        })
            .catch(() => {
            this.updateResults(term, true, term !== '');
            this.onDismiss();
        });
    }
    handleAnnounceResults(results) {
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }
        // Delay by 1 second to ensure live announce is heard
        if (results.length > 0) {
            this.typingTimeout = setTimeout(() => {
                this.announceResults(results);
            }, 1000);
        }
    }
    handleKeyPress(event) {
        const { value } = this.props;
        const resultsToShow = filterTypeaheadResults(this.getResultsToShow(), Array.isArray(value) ? value : []);
        const { activeSuggestion, searchTerm } = this.state;
        if (event.key === 'Enter' || event.keyCode === 38 || event.keyCode === 40) {
            event.preventDefault();
        }
        if (event.key === 'Enter') {
            const selected = selectTypeaheadResultIndex(resultsToShow, activeSuggestion);
            selected
                ? this.handleResultItemSelect(selected.value, selected.data, resultsToShow)
                : this.handleResultItemSelect(searchTerm, undefined, resultsToShow);
        }
    }
    handleActiveSuggestion(direction) {
        const { activeSuggestion, results, searchTerm } = this.state;
        const { allowExactMatch } = this.props;
        // Extra list item in case of allowExactMatch true
        const extraListItemForSearch = allowExactMatch && searchTerm ? 1 : 0;
        const resultsLength = results.length + extraListItemForSearch;
        let circularListFactor = 0;
        if (direction === TypeaheadNavKeys$1.UP) {
            circularListFactor = activeSuggestion === -1 ? resultsLength : resultsLength - 1;
        }
        else if (direction === TypeaheadNavKeys$1.DOWN) {
            circularListFactor = resultsLength + 1;
        }
        const activePosition = (activeSuggestion + circularListFactor) % resultsLength;
        const activePositionTab = (this.newAttr + circularListFactor) % resultsLength;
        this.setState({
            activeSuggestion: this.newAttr > -1 ? activePositionTab : activePosition,
        });
        this.newAttr = -1;
    }
    handleUpArrowKey(e) {
        if (e.keyCode === 38) {
            this.handleActiveSuggestion(TypeaheadNavKeys$1.UP);
        }
    }
    handleDownArrowKey(e) {
        if (e.keyCode === 40) {
            this.handleActiveSuggestion(TypeaheadNavKeys$1.DOWN);
        }
    }
    handleTabKey(e) {
        if (e.keyCode === 9 || (e.shiftKey && e.keyCode === 9)) {
            const attr = e.target.parentElement.getAttribute('data-index');
            if (attr) {
                this.newAttr = Number(attr);
            }
            else {
                this.newAttr = -1;
            }
        }
    }
    handleEscKey(e) {
        if (e.keyCode === 27) {
            this.onDismiss();
        }
    }
    handleKeyDown(e) {
        this.handleDownArrowKey(e);
        this.handleUpArrowKey(e);
        this.handleEscKey(e);
    }
    handleKeyUp(e) {
        this.handleTabKey(e);
    }
    onDismiss() {
        const { onDismiss, isMulti, l10n } = this.props;
        this.contentRef.current.scrollTop = 0;
        if (onDismiss) {
            onDismiss();
        }
        // To reset activeSuggestion when typeahead closed
        this.resetActiveSuggestion();
        // eslint-disable-next-line react/no-unused-state
        this.setState({ startSearchTerm: '' });
        if (isMulti) {
            setTimeout(() => {
                liveAnnounce(l10n.formatText('typeahead.announceResults.menuClosed'));
            }, 100);
        }
        this.removeDialogLiveAnnounce();
    }
    getResultsToShow() {
        const { categorize } = this.props;
        const { categorizedResults, results } = this.state;
        return categorize || (categorizedResults && categorizedResults.length)
            ? {
                items: categorizedResults,
                type: 'categorized',
            }
            : {
                items: results,
                type: 'uncategorized',
            };
    }
    removeDialogLiveAnnounce() {
        const liveAnnounceNode = document.getElementById(this.typeaheadDialogLiveAnnounceId);
        if (liveAnnounceNode) {
            liveAnnounceNode.parentNode?.removeChild(liveAnnounceNode);
        }
    }
    resetActiveSuggestion() {
        this.setState({
            activeSuggestion: -1,
        });
    }
    updateInput(term, makeAltDestCall) {
        this.setState({ searchTerm: term }, () => {
            this.updateResults(term, false, !!makeAltDestCall);
            setTimeout(() => {
                this.selectInput();
            });
        });
    }
    scrollTop() {
        if (navigator.userAgent.match(this.iphoneSafariUARegex)) {
            if (document.getElementsByTagName('html')[0].scrollTop > 0) {
                document.getElementsByTagName('html')[0].scrollTo({ top: 0, behavior: 'smooth' });
            }
        }
    }
    openDialog() {
        const { searchTerm } = this.state;
        const { adapter, startSearchTerm, defaultTerm } = this.props;
        adapter.typeaheadDialogOpened(searchTerm);
        this.updateInput(searchTerm || defaultTerm || startSearchTerm || '', searchTerm !== '');
        this.scrollTop();
    }
    selectInput() {
        const { toolbarInputRef } = this;
        if (toolbarInputRef && toolbarInputRef.current && typeof toolbarInputRef.current.select === 'function') {
            toolbarInputRef.current.select();
        }
    }
    focusInput() {
        const { toolbarInputRef } = this;
        if (toolbarInputRef && toolbarInputRef.current && typeof toolbarInputRef.current.focus === 'function') {
            toolbarInputRef.current.focus();
        }
    }
    announceResults(results) {
        const { searchTerm } = this.state;
        const { l10n, label, defaultTerm } = this.props;
        const searchChars = searchTerm && searchTerm.split('');
        if (searchChars) {
            const outputWithQuotes = `'${searchChars.join("' , '")} '`;
            if (results) {
                liveAnnounce(l10n.formatText(`typeahead.announceResults.resultsAvailable`, outputWithQuotes, results.length), undefined, undefined, this.contentRef, this.typeaheadDialogLiveAnnounceId);
            }
        }
        else if (defaultTerm) {
            if (results) {
                liveAnnounce(l10n.formatText(`typeahead.announceResults.showingResultsDefault`, label, results.length));
            }
        }
    }
    isCategorizedResult(result) {
        return typeof result === 'object' && 'heading' in result;
    }
    async updateResults(term, makeRecentHistoryCall, makeAltDestCall) {
        const { adapter, categorize, defaultTerm } = this.props;
        const { isCurrentLocation } = this.state;
        const termOrDefault = term === '' || isCurrentLocation ? defaultTerm ?? '' : term;
        this.lastSearchTerm = termOrDefault;
        let results;
        let categorizedResults;
        if (termOrDefault !== '' && makeRecentHistoryCall === true) {
            const [queryResults, recentHistoryResults] = categorize
                ? await Promise.all([
                    adapter.fetchResultsCategorized(termOrDefault),
                    adapter.fetchRecentSearchResultsCategorized(),
                ])
                : await Promise.all([adapter.fetchResults(termOrDefault), adapter.fetchRecentSearchResults()]);
            if (recentHistoryResults && recentHistoryResults.length > 0) {
                categorize || this.isCategorizedResult(recentHistoryResults[0])
                    ? (categorizedResults = recentHistoryResults)
                    : (results = recentHistoryResults);
            }
            else {
                categorize ? (categorizedResults = queryResults) : (results = queryResults);
            }
        }
        else if (categorize) {
            categorizedResults = await adapter.fetchResultsCategorized(termOrDefault);
        }
        else if (termOrDefault !== '' && !!makeAltDestCall) {
            const tempResults = await adapter.fetchAlternativeResults(termOrDefault);
            tempResults && tempResults.length && this.isCategorizedResult(tempResults[0])
                ? (categorizedResults = tempResults)
                : (results = tempResults);
        }
        else {
            const tempResults = await adapter.fetchResults(termOrDefault);
            tempResults && tempResults.length && this.isCategorizedResult(tempResults[0])
                ? (categorizedResults = tempResults)
                : (results = tempResults);
        }
        if (this.lastSearchTerm !== termOrDefault) {
            return;
        }
        if (results && results.length && this._isMounted) {
            adapter.logSuggestionShown(makeRecentHistoryCall || makeAltDestCall ? '' : termOrDefault, results);
            this.setState({ results, categorizedResults: [] }, () => {
                this.handleAnnounceResults(results);
            });
        }
        else if (categorizedResults && categorizedResults.length) {
            adapter.logSuggestionShown(makeRecentHistoryCall || makeAltDestCall ? '' : termOrDefault, categorizedResults);
            const allResults = categorizedResults.reduce((finalArray, currentResult) => {
                finalArray.push(...currentResult.typeaheadResults);
                return finalArray;
            }, []);
            this.setState({ categorizedResults, results: allResults }, () => {
                this.handleAnnounceResults(categorizedResults);
            });
        }
        else if (this._isMounted) {
            this.setState({ results: [], categorizedResults: [] });
        }
    }
    render() {
        const { allowExactMatch, dialog, id, listTitle, stid, placeholder, label, handleClear, emptyResultPlaceholder, isMulti, dismissButtonText, clearAllText, onClearAll, onItemRemove, selectedItemHeading, value, strictFullscreen, typeaheadInputRef, required, } = this.props;
        const { activeSuggestion, searchTerm } = this.state;
        const resultsToShow = this.getResultsToShow();
        return (jsx(EGDSSheetTransition, { isVisible: dialog, openCallback: this.openDialog, children: jsx(EGDSSheet, { className: getClassNameList$3('uitk-typeahead-dialog', {
                    'uitk-typeahead-dialog-centered': !strictFullscreen,
                }), isVisible: dialog, type: strictFullscreen ? 'full' : 'centered', triggerRef: typeaheadInputRef, focusElemRef: this.toolbarInputRef, children: jsxs(EGDSLayoutFlex, { direction: "column", blockSize: "full_size", children: [jsx(EGDSLayoutFlexItem, { children: jsx(EGDSToolbar, { className: getClassNameList$3('uitk-typeahead-input-wrapper'), navigationContent: {
                                    navIconLabel: 'Close the dialog',
                                    onClick: this.onDismiss,
                                    navButtonId: `${id}-close`,
                                }, children: jsx(EGDSTypeaheadInput, { className: getClassNameList$3('uitk-typeahead-input-v2'), "data-stid": stid && `${stid}-dialog-input`, domRef: this.toolbarInputRef, hasClearButton: searchTerm.length > 0, hasFloatedLabel: false, hasVisibleLabel: false, id: stid, label: label, onChange: this.handleChange, onKeyPress: this.handleKeyPress, placeholder: placeholder, value: searchTerm, onKeyDown: this.handleKeyDown, onKeyUp: this.handleKeyUp, handleClear: handleClear, required: required, clearButtonLabel: searchTerm }) }) }), jsx(EGDSLayoutFlexItem, { children: jsx(EGDSSheetContent, { className: getClassNameList$3('uitk-typeahead-dialog-content'), contentDomRef: this.contentRef, padded: false, children: jsxs(EGDSLayoutFlex, { direction: "column", blockSize: "full_size", justifyContent: "space-between", children: [jsx(EGDSLayoutFlexItem, { children: jsx(EGDSTypeaheadResults, { allowExactMatch: allowExactMatch, typeaheadLabel: label, onResultItemSelect: this.handleResultItemSelect, results: resultsToShow, searchTerm: searchTerm, stid: stid, listTitle: listTitle, activeSuggestion: activeSuggestion, emptyResultPlaceholder: emptyResultPlaceholder, isMulti: isMulti, dismissButtonText: dismissButtonText, selectedItemHeading: selectedItemHeading, clearAllText: clearAllText, onClearAll: onClearAll, onItemRemove: onItemRemove, value: value, contentRef: this.contentRef, historyDetail: this.props.historyDetail }) }), this.props.isMulti ? (jsx(EGDSLayoutFlexItem, { children: jsx(EGDSScrim, { type: EGDSScrimType.BUTTON, children: jsx(EGDSFloatingFullWidthButton, { disabled: value?.length === 0, onClick: this.onDismiss, isFullWidth: true, children: dismissButtonText }) }) })) : null] }) }) })] }) }) }));
    }
}
const EGDSTypeaheadDialog = (props) => {
    const l10n = useLocalization();
    return jsx(TypeaheadDialog, { l10n: l10n, ...props });
};

const getClassNameList$2 = classNames.bind(styles);
class EGDSTypeaheadInput extends React.Component {
    static displayName = 'EGDSTypeaheadInput';
    render() {
        const { className, placeholder, required, ...props } = this.props;
        const cssClassNames = getClassNameList$2('uitk-typeahead-input', className);
        return (jsx(EGDSInput, { ...props, autoCapitalize: "off", autoComplete: "off", autoCorrect: "off", className: cssClassNames, type: "text", spellCheck: false, placeholder: placeholder, required: required }));
    }
}

const getClassNameList$1 = classNames.bind(styles);
const isValueEmpty = (value) => value === '' || (Array.isArray(value) && value.length === 0);
var TypeaheadNavKeys;
(function (TypeaheadNavKeys) {
    TypeaheadNavKeys["UP"] = "UP";
    TypeaheadNavKeys["DOWN"] = "DOWN";
})(TypeaheadNavKeys || (TypeaheadNavKeys = {}));
class TypeaheadMenu extends React.Component {
    static placeholder = [
        { disabled: true, icon: 'brightness_1', name: ' ', value: ' ', title: ' ' },
    ];
    keyPressed = false;
    newAttr = -1;
    lastSearchTerm;
    toolbarInputRef = React.createRef();
    typingTimeout = 0;
    resultsDomRef;
    updateResultsDebounced;
    constructor(props) {
        super(props);
        const { dialog, term, debounceRate } = this.props;
        this.state = {
            activeSuggestion: -1,
            categorizedResults: [],
            // eslint-disable-next-line react/no-unused-state
            data: {},
            // eslint-disable-next-line react/no-unused-state
            menu: dialog,
            results: [],
            searchTerm: term,
            showResults: true,
        };
        this.handleChange = this.handleChange.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleResultItemSelect = this.handleResultItemSelect.bind(this);
        this.focusInput = this.focusInput.bind(this);
        this.announceResults = this.announceResults.bind(this);
        this.onDismiss = this.onDismiss.bind(this);
        this.updateResults = this.updateResults.bind(this);
        this.updateResultsDebounced =
            debounceRate && debounceRate > 0 ? debounce(debounceRate, this.updateResults) : this.updateResults;
        this.menuOpened = this.menuOpened.bind(this);
        this.menuClosed = this.menuClosed.bind(this);
        this.resetActiveSuggestion = this.resetActiveSuggestion.bind(this);
        this.resultsDomRef = React.createRef();
    }
    componentDidUpdate(prevProps, prevState) {
        const { searchTerm, results, showResults } = this.state;
        const { label, l10n, value, defaultTerm } = this.props;
        const searchLen = results && results.length;
        const isTypeAheadFocused = this.isTypeAheadFocused();
        if (isValueEmpty(value) && isValueEmpty(searchTerm) && isTypeAheadFocused && !defaultTerm) {
            liveAnnounce(l10n.formatText(`typeahead.announceResults.trigerLabel`, label));
        }
        if (prevState.searchTerm === searchTerm &&
            !isValueEmpty(value) &&
            this.keyPressed === false &&
            isTypeAheadFocused) {
            liveAnnounce(l10n.formatText(`typeahead.announceResults.showingResults`, label, searchLen, searchTerm));
        }
        if (prevState.searchTerm !== searchTerm) {
            if (showResults) {
                this.setState({
                    results: TypeaheadMenu.placeholder,
                });
                this.updateResultsDebounced(searchTerm);
            }
        }
        this.focusInput();
    }
    handleFocus() {
        this.selectInput();
    }
    handleResultItemSelect(term, data, results) {
        const { adapter, isMulti, onResultItemSelect, label, l10n } = this.props;
        const { searchTerm } = this.state;
        return adapter
            .onItemSelection?.(data)
            .then((result) => {
            if (isMulti) {
                this.setState({
                    activeSuggestion: -1,
                });
            }
            else {
                this.setState({
                    activeSuggestion: -1,
                    searchTerm: term,
                    showResults: false,
                });
            }
            onResultItemSelect(term, result);
            // Results is the results prior to us triggering onResulItemSelect so we subtract 1
            // This is technically not correct because the typeahead is controlled and users could decide to not select the value (or do any other logic) in onChange even if that would not make sense.  Ideally liveAnnnounce logic would trigger based on state change and not on events
            const searchLen = results ? getSizeOfTypeaheadResults(results) - 1 : 0;
            if (isMulti) {
                liveAnnounce(`${l10n.formatText(`typeahead.announceResults.itemSelected`, term)}
             ${l10n.formatText('typeahead.announceResults.showingResults', label, searchLen, searchTerm)}`);
            }
            if (!isMulti) {
                setTimeout(() => {
                    liveAnnounce(`${l10n.formatText(`typeahead.announceResults.itemSelected`, term)} 
            ${l10n.formatText('typeahead.announceResults.menuClosed')}`);
                }, 100);
                this.menuClosed();
            }
        })
            .catch(() => {
            this.updateResults(term, true, term !== '');
            this.menuClosed();
        });
    }
    handleAnnounceResults(results) {
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }
        // Delay by 1 second
        // to ensure live announce is heard
        if (results?.length > 0) {
            this.typingTimeout = setTimeout(() => {
                this.announceResults(results);
            }, 1000);
        }
    }
    handleKeyPress(event) {
        const { activeSuggestion, searchTerm } = this.state;
        const { value, l10n } = this.props;
        this.keyPressed = true;
        const resultsToShow = filterTypeaheadResults(this.getResultsToShow(), Array.isArray(value) ? value : []);
        const searchLen = resultsToShow && getSizeOfTypeaheadResults(resultsToShow);
        this.keyPressed = true;
        const searchChars = searchTerm && searchTerm.split('');
        if (searchChars) {
            const outputWithQuotes = `'${searchChars.join("' , '")} '`;
            if (searchTerm !== '' && searchLen > 0) {
                liveAnnounce(l10n.formatText(`typeahead.announceResults.resultsAvailable`, outputWithQuotes, searchLen));
            }
        }
        if (event.key === 'Enter' || event.keyCode === 38) {
            event.preventDefault();
        }
        if (event.key === 'Enter') {
            const selected = selectTypeaheadResultIndex(resultsToShow, activeSuggestion);
            selected
                ? this.handleResultItemSelect(selected.value, selected.data, resultsToShow)
                : this.handleResultItemSelect(searchTerm, undefined, resultsToShow);
        }
    }
    handleChange(event) {
        const { onInputChange, updateCachedSearchTerm } = this.props;
        this.setState({ searchTerm: event.target.value, showResults: true }, this.focusInput);
        // To reset activeSuggestion when value is cleared
        if (event.target.value === '') {
            this.resetActiveSuggestion();
        }
        onInputChange && onInputChange(event);
        updateCachedSearchTerm && updateCachedSearchTerm(event);
    }
    handleActiveSuggestion(direction) {
        const { activeSuggestion, results, searchTerm } = this.state;
        const { allowExactMatch } = this.props;
        // Extra list item in case of allowExactMatch true
        const extraListItemForSearch = allowExactMatch && searchTerm ? 1 : 0;
        const resultsLength = results.length + extraListItemForSearch;
        let circularListFactor = 0;
        if (direction === TypeaheadNavKeys.UP) {
            circularListFactor = activeSuggestion === -1 ? resultsLength : resultsLength - 1;
        }
        else if (direction === TypeaheadNavKeys.DOWN) {
            circularListFactor = resultsLength + 1;
        }
        const activePosition = (activeSuggestion + circularListFactor) % resultsLength;
        const activePositionTab = (this.newAttr + circularListFactor) % resultsLength;
        this.setState({
            activeSuggestion: this.newAttr > -1 ? activePositionTab : activePosition,
        });
        this.newAttr = -1;
    }
    handleUpArrowKey(e) {
        if (e.keyCode === 38) {
            e.preventDefault();
            this.handleActiveSuggestion(TypeaheadNavKeys.UP);
        }
    }
    handleDownArrowKey(e) {
        if (e.keyCode === 40) {
            this.keyPressed = true;
            this.handleActiveSuggestion(TypeaheadNavKeys.DOWN);
        }
    }
    handleTabKey(e) {
        if (e.keyCode === 9 || (e.shiftKey && e.keyCode === 9)) {
            const attr = e.target.parentElement.getAttribute('data-index');
            if (attr) {
                this.newAttr = Number(attr);
            }
            else {
                this.newAttr = -1;
            }
        }
    }
    handleEscKey(e) {
        if (e.keyCode === 27) {
            this.onDismiss();
        }
    }
    handleKeyDown(e) {
        this.handleDownArrowKey(e);
        this.handleUpArrowKey(e);
        this.handleEscKey(e);
    }
    handleKeyUp(e) {
        this.handleTabKey(e);
    }
    onDismiss() {
        const { isMulti, onDismiss, l10n } = this.props;
        this.keyPressed = false;
        if (this.resultsDomRef.current) {
            this.resultsDomRef.current.scrollTop = 0;
        }
        if (onDismiss) {
            onDismiss();
        }
        if (isMulti) {
            liveAnnounce(l10n.formatText('typeahead.announceResults.menuClosed'));
        }
    }
    getResultsToShow() {
        const { categorize } = this.props;
        const { categorizedResults, results } = this.state;
        return categorize || (categorizedResults && categorizedResults.length)
            ? {
                items: categorizedResults,
                type: 'categorized',
            }
            : {
                items: results,
                type: 'uncategorized',
            };
    }
    static getDerivedStateFromProps(nextProps, prevState) {
        const term = nextProps.term !== prevState.searchTerm && !nextProps.dialog ? nextProps.term : prevState.searchTerm;
        if (!nextProps.isMulti) {
            return {
                searchTerm: term,
            };
        }
        return null;
    }
    menuClosed() {
        const { adapter, onDismiss } = this.props;
        if (adapter && typeof adapter.logTypeaheadDismiss === 'function') {
            adapter.logTypeaheadDismiss();
        }
        if (onDismiss) {
            onDismiss();
        }
        // To reset activeSuggestion when menu is closed
        this.resetActiveSuggestion();
    }
    menuOpened() {
        const { defaultTerm } = this.props;
        const { searchTerm } = this.state;
        if (this.resultsDomRef.current) {
            this.resultsDomRef.current.scrollTop = 0;
        }
        this.selectInput();
        this.updateResults(!searchTerm && defaultTerm ? defaultTerm : searchTerm, false, searchTerm !== '');
    }
    announceResults(results) {
        const { searchTerm } = this.state;
        const { l10n, label, defaultTerm } = this.props;
        const searchChars = searchTerm && searchTerm.split('');
        if (searchChars) {
            const outputWithQuotes = `'${searchChars.join("' , '")} '`;
            if (results && this.isTypeAheadFocused()) {
                liveAnnounce(l10n.formatText(`typeahead.announceResults.resultsAvailable`, outputWithQuotes, results.length));
            }
        }
        else if (defaultTerm) {
            if (results && this.isTypeAheadFocused()) {
                liveAnnounce(l10n.formatText(`typeahead.announceResults.showingResultsDefault`, label, results.length));
            }
        }
    }
    isCategorizedResult(result) {
        return 'heading' in result;
    }
    async updateResults(term, makeRecentHistoryCall, makeAltDestCall) {
        this.lastSearchTerm = term;
        const { adapter, categorize, defaultTerm } = this.props;
        let results;
        let categorizedResults;
        let queryResults;
        let recentHistoryResults;
        const termOrDefault = term === '' ? defaultTerm ?? '' : term;
        if (termOrDefault !== '' && !!makeRecentHistoryCall) {
            [queryResults, recentHistoryResults] = categorize
                ? await Promise.all([
                    adapter.fetchResultsCategorized(termOrDefault),
                    adapter.fetchRecentSearchResultsCategorized(),
                ])
                : await Promise.all([adapter.fetchResults(termOrDefault), adapter.fetchRecentSearchResults()]);
            if (recentHistoryResults && recentHistoryResults.length > 0) {
                categorize || this.isCategorizedResult(recentHistoryResults[0])
                    ? (categorizedResults = recentHistoryResults)
                    : (results = recentHistoryResults);
            }
            else {
                categorize ? (categorizedResults = queryResults) : (results = queryResults);
            }
        }
        else if (categorize) {
            categorizedResults = await adapter.fetchResultsCategorized(termOrDefault);
        }
        else if (termOrDefault !== '' && !!makeAltDestCall) {
            const tempResults = await adapter.fetchAlternativeResults(termOrDefault);
            tempResults && tempResults.length && this.isCategorizedResult(tempResults[0])
                ? (categorizedResults = tempResults)
                : (results = tempResults);
        }
        else {
            const tempResults = await adapter.fetchResults(termOrDefault);
            tempResults && tempResults.length && this.isCategorizedResult(tempResults[0])
                ? (categorizedResults = tempResults)
                : (results = tempResults);
        }
        if (this.lastSearchTerm !== term) {
            return;
        }
        if (results && results.length) {
            adapter.logSuggestionShown(makeRecentHistoryCall || makeAltDestCall ? '' : term, results);
            this.setState({ results, categorizedResults: [] }, () => {
                this.handleAnnounceResults(results);
            });
        }
        else if (categorizedResults && categorizedResults.length) {
            adapter.logSuggestionShown(makeRecentHistoryCall || makeAltDestCall ? '' : term, categorizedResults);
            const allResults = categorizedResults.flatMap((result) => result.typeaheadResults);
            this.setState({ categorizedResults, results: allResults }, () => {
                this.handleAnnounceResults(results);
            });
        }
        else {
            this.setState({ results: [], categorizedResults: [] });
        }
    }
    selectInput() {
        const { toolbarInputRef } = this;
        if (toolbarInputRef && toolbarInputRef.current && typeof toolbarInputRef.current.select === 'function') {
            toolbarInputRef.current.select();
        }
    }
    focusInput() {
        const { toolbarInputRef } = this;
        if (toolbarInputRef &&
            toolbarInputRef.current &&
            typeof toolbarInputRef.current.focus === 'function' &&
            isElementVisible(this.toolbarInputRef.current)) {
            toolbarInputRef.current.focus();
        }
    }
    resetActiveSuggestion() {
        this.setState({
            activeSuggestion: -1,
        });
    }
    isTypeAheadFocused() {
        return this.toolbarInputRef.current === document.activeElement;
    }
    render() {
        const { allowExactMatch, children, dialog, stid, label, listTitle, placeholder, handleClear, emptyResultPlaceholder, dismissButtonText, value, isMulti, clearAllText, onClearAll, onItemRemove, selectedItemHeading, typeaheadInputRef, historyDetail, required, } = this.props;
        const { activeSuggestion, searchTerm } = this.state;
        const resultsToShow = this.getResultsToShow();
        return (jsx("div", { className: getClassNameList$1('uitk-menu-typeahead-wrapper'), children: jsxs("div", { className: getClassNameList$1('uitk-typeahead-menu'), id: stid && `${stid}-menu`, children: [jsx("div", { className: getClassNameList$1('uitk-menu-trigger'), children: children }), jsx(EGDSSheetTransition, { isVisible: dialog, openCallback: this.menuOpened, children: jsx(EGDSSheet, { autoFocus: false, type: "popover", className: getClassNameList$1('uitk-typeahead-menu-container', {
                                'is-multi': isMulti,
                            }), disableArrowKeyHandler: true, isVisible: dialog, onDismiss: this.menuClosed, triggerRef: typeaheadInputRef, showOverTrigger: true, autoPosition: false, children: jsxs(EGDSLayoutFlex, { direction: "column", blockSize: "full_size", children: [jsx(EGDSLayoutFlexItem, { children: jsx("div", { className: getClassNameList$1('uitk-typeahead-input-wrapper'), children: jsx(EGDSTypeaheadInput, { className: getClassNameList$1('uitk-typeahead-input-v2'), "data-stid": stid && `${stid}-menu-input`, domRef: this.toolbarInputRef, handleClear: handleClear, hasClearButton: searchTerm.length > 0, hasFloatedLabel: false, hasVisibleLabel: false, id: stid, label: label, onChange: this.handleChange, onKeyPress: this.handleKeyPress, onKeyDown: this.handleKeyDown, onKeyUp: this.handleKeyUp, placeholder: placeholder, required: required, value: searchTerm, clearButtonLabel: searchTerm }) }) }), jsx(EGDSLayoutFlexItem, { children: jsx(EGDSSheetContent, { className: getClassNameList$1('uitk-typeahead-menu-content'), padded: false, children: jsx(EGDSTypeaheadResults, { typeaheadLabel: label, allowExactMatch: allowExactMatch, onResultItemSelect: this.handleResultItemSelect, results: resultsToShow, searchTerm: searchTerm, stid: stid, listTitle: listTitle, activeSuggestion: activeSuggestion, emptyResultPlaceholder: emptyResultPlaceholder, isMulti: isMulti, dismissButtonText: dismissButtonText, selectedItemHeading: selectedItemHeading, clearAllText: clearAllText, onClearAll: onClearAll, onItemRemove: onItemRemove, value: value, domRef: this.resultsDomRef, historyDetail: historyDetail }) }) }), isMulti && (jsx(EGDSLayoutFlexItem, { children: jsx(EGDSLayoutFlex, { justifyContent: "end", className: getClassNameList$1('uitk-typeahead-menu-footer'), children: jsx(EGDSLayoutFlexItem, { children: jsx(EGDSPrimaryButton, { disabled: value?.length === 0, onClick: this.onDismiss, children: dismissButtonText }) }) }) }))] }) }) })] }) }));
    }
}
const EGDSTypeaheadMenu = (props) => {
    const l10n = useLocalization();
    return jsx(TypeaheadMenu, { l10n: l10n, ...props });
};

const getClassNameList = classNames.bind(styles);
const l10nStrings = {
    enteringInput: 'typeahead.enteringInput',
};
var TypeaheadRenderType;
(function (TypeaheadRenderType) {
    TypeaheadRenderType["MENU"] = "menu";
    TypeaheadRenderType["DIALOG"] = "dialog";
    TypeaheadRenderType["ADAPTIVE"] = "adaptive";
})(TypeaheadRenderType || (TypeaheadRenderType = {}));
class Typeahead extends React.Component {
    static displayName = 'EGDSTypeahead';
    static defaultProps = {
        hasClearButton: false,
        onInputChange: () => null,
        renderType: TypeaheadRenderType.ADAPTIVE,
        isMulti: false,
    };
    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.isMulti) {
            return {
                ...prevState,
                searchTerm: '',
            };
        }
        if (nextProps.value !== prevState.searchTerm) {
            return {
                ...prevState,
                searchTerm: nextProps.value ?? '',
            };
        }
        return null;
    }
    keyArray = [];
    typeaheadInputRef;
    constructor(props) {
        super(props);
        this.state = {
            dialog: false,
            mounted: false,
            searchTerm: '',
            startSearchTerm: '',
            cachedSearchTerm: '',
        };
        if (props.isMulti && !Array.isArray(props.value)) {
            console.warn('Use an array of values if set to isMulti');
        }
        this.typeaheadInputRef = React.createRef();
        this.handleResultItemSelect = this.handleResultItemSelect.bind(this);
        this.dismissTypeahead = this.dismissTypeahead.bind(this);
        this.openTypeahead = this.openTypeahead.bind(this);
        this.renderDialog = this.renderDialog.bind(this);
        this.renderMenu = this.renderMenu.bind(this);
        this.handleClear = this.handleClear.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.focusTypeaheadInput = this.focusTypeaheadInput.bind(this);
        this.renderMenuTypeahead = this.renderMenuTypeahead.bind(this);
        this.renderDialogTypeahead = this.renderDialogTypeahead.bind(this);
        this.updateStartSearchTerm = this.updateStartSearchTerm.bind(this);
        this.updateCachedSearchTerm = this.updateCachedSearchTerm.bind(this);
    }
    componentDidMount() {
        const { value, onMountCB, isMulti } = this.props;
        if (Array.isArray(value) && isMulti) {
            this.setState({ searchTerm: value.join(', ') });
        }
        this.setState({ mounted: true });
        if (onMountCB) {
            onMountCB(this.typeaheadInputRef);
        }
    }
    handleKeyPress(e, triggerType) {
        const code = e.keyCode || e.which;
        const keyValue = e.key;
        if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
            this.keyArray.push(keyValue);
            const joinedKeyArray = this.keyArray.join('');
            if (triggerType === TypeaheadRenderType.DIALOG) {
                this.updateStartSearchTerm(triggerType, joinedKeyArray);
            }
        }
    }
    handleClear() {
        const { handleClear } = this.props;
        this.setState({
            searchTerm: '',
        });
        if (typeof handleClear === 'function') {
            handleClear();
        }
    }
    handleResultItemSelect(term, data) {
        const { onUpdate, isMulti, onResultSelection, id } = this.props;
        if (onUpdate) {
            onUpdate({ term, data });
        }
        if (onResultSelection) {
            const event = new CustomEvent('change');
            const input = document.createElement('input');
            input.value = term;
            if ((data && data.id) || id) {
                input.setAttribute('id', data.id ?? id);
            }
            input.addEventListener('change', (inputEvent) => {
                onResultSelection(inputEvent);
            });
            input.dispatchEvent(event);
        }
        if (!isMulti) {
            this.setState({
                searchTerm: term,
            });
            this.focusTypeaheadInput();
        }
    }
    getAdapter() {
        const { adapter, l10n } = this.props;
        if (adapter instanceof ESSAdapter) {
            adapter.setL10n(l10n);
        }
        return adapter;
    }
    dismissTypeahead(triggerType) {
        const { onDismiss } = this.props;
        const isVisible = isElementVisible(this.typeaheadInputRef.current);
        if (typeof onDismiss === 'function') {
            onDismiss(triggerType);
        }
        this.setState({ dialog: false, startSearchTerm: '' });
        this.keyArray = [];
        if (this.typeaheadInputRef.current && isVisible) {
            this.typeaheadInputRef.current.focus();
        }
    }
    updateStartSearchTerm(triggerType, keyValue) {
        this.setState({ startSearchTerm: keyValue }, () => {
            if (triggerType === 'dialog') {
                this.openTypeahead(triggerType);
            }
        });
    }
    openTypeahead(triggerType) {
        const { adapter, onClick } = this.props;
        const { dialog, searchTerm } = this.state;
        const previousState = dialog;
        // To handle menu open/close
        if (triggerType === TypeaheadRenderType.MENU) {
            if (previousState) {
                this.dismissTypeahead(triggerType);
            }
            else {
                if (typeof onClick === 'function') {
                    onClick(triggerType);
                }
                this.setState({ dialog: true });
                setTimeout(() => {
                    adapter.typeaheadDialogOpened(searchTerm);
                });
            }
        }
        // To handle dialog open/close
        else if (triggerType === TypeaheadRenderType.DIALOG) {
            if (typeof onClick === 'function') {
                onClick(triggerType);
            }
            this.setState({ dialog: true });
        }
    }
    focusTypeaheadInput() {
        const { l10n } = this.props;
        liveAnnounce(l10n.formatText(l10nStrings.enteringInput));
        if (this.typeaheadInputRef.current) {
            this.typeaheadInputRef.current.focus();
        }
    }
    updateCachedSearchTerm(e) {
        this.setState({ cachedSearchTerm: e.target.value });
    }
    filterLayoutPositionClassName(className, include) {
        return className?.split(' ').filter((c) => c.startsWith('uitk-layout-position') === include);
    }
    renderMenu() {
        const { allowExactMatch, label, listTitle, categorize, onInputChange, stid, typeaheadPlaceholder, typeaheadEmptyResultPlaceholder, value, isMulti = false, customTrigger, dismissButtonText, clearAllText, handleClear, onItemRemove, selectedItemHeading, l10n, defaultTerm, debounceRate, required, } = this.props;
        const additionalProps = {
            onDismiss: () => {
                this.dismissTypeahead('menu');
            },
        };
        const { dialog, searchTerm, cachedSearchTerm } = this.state;
        const adapter = this.getAdapter();
        if (categorize) {
            adapter.setTypeaheadCategories();
        }
        return (jsx(EGDSTypeaheadMenu, { ...additionalProps, isMulti: isMulti, value: value, label: label, adapter: adapter, dialog: (customTrigger && customTrigger.showTypeahead) || dialog, allowExactMatch: allowExactMatch, onInputChange: onInputChange, onResultItemSelect: this.handleResultItemSelect, stid: stid, term: (searchTerm || cachedSearchTerm) ?? defaultTerm, placeholder: typeaheadPlaceholder, emptyResultPlaceholder: typeaheadEmptyResultPlaceholder, listTitle: listTitle, categorize: categorize, handleClear: this.handleClear, clearAllText: clearAllText, onClearAll: handleClear, dismissButtonText: dismissButtonText ?? l10n.formatText('typeahead.multiselect.dismiss'), onItemRemove: onItemRemove, selectedItemHeading: selectedItemHeading, defaultTerm: defaultTerm, debounceRate: debounceRate, typeaheadInputRef: this.typeaheadInputRef, historyDetail: adapter.isHistoryDetailEnabled(), required: required, updateCachedSearchTerm: this.updateCachedSearchTerm }));
    }
    renderDialog() {
        const { allowExactMatch, id, label, listTitle, categorize, onInputChange, stid, typeaheadPlaceholder, typeaheadEmptyResultPlaceholder, customTrigger, defaultTerm, isMulti = false, clearAllText, handleClear, dismissButtonText, selectedItemHeading, onItemRemove, value, l10n, debounceRate, strictFullscreen, required, } = this.props;
        const { dialog, searchTerm, startSearchTerm, cachedSearchTerm } = this.state;
        const adapter = this.getAdapter();
        if (categorize) {
            adapter.setTypeaheadCategories();
        }
        const additionalProps = {
            onDismiss: () => {
                this.dismissTypeahead(TypeaheadRenderType.DIALOG);
            },
        };
        return (jsx(EGDSTypeaheadDialog, { adapter: adapter, allowExactMatch: allowExactMatch, dialog: (customTrigger && customTrigger.showTypeahead) || dialog, id: id, label: label, ...additionalProps, onInputChange: onInputChange, onResultItemSelect: this.handleResultItemSelect, term: searchTerm || cachedSearchTerm, startSearchTerm: startSearchTerm, stid: stid, placeholder: typeaheadPlaceholder, emptyResultPlaceholder: typeaheadEmptyResultPlaceholder, listTitle: listTitle, categorize: categorize, handleClear: this.handleClear, defaultTerm: defaultTerm, isMulti: isMulti, clearAllText: clearAllText, onClearAll: handleClear, dismissButtonText: dismissButtonText ?? l10n.formatText('typeahead.multiselect.dismiss'), onItemRemove: onItemRemove, selectedItemHeading: selectedItemHeading, value: value, debounceRate: debounceRate, strictFullscreen: strictFullscreen, typeaheadInputRef: this.typeaheadInputRef, historyDetail: adapter.isHistoryDetailEnabled(), required: required, updateCachedSearchTerm: this.updateCachedSearchTerm }));
    }
    renderTrigger(triggerType) {
        const { accessibilityLabel, analytics, icon, hasClearButton, hasVisibleLabel, invalid, label, name, placeholder, stid, customTrigger, children, isMulti, value, required, className, triggerComponentWidth, } = this.props;
        const { mounted, searchTerm, dialog } = this.state;
        const a11yLabel = accessibilityLabel || label;
        const TriggerTag = triggerType === 'menu' ? EGDSTextInput : EGDSTypeaheadInput;
        const layoutPositionClassName = this.filterLayoutPositionClassName(className, true)?.join(' ');
        if (customTrigger) {
            return children;
        }
        const triggerCompStyle = {
            ...(triggerComponentWidth && { width: triggerComponentWidth }),
        };
        return (jsxs("div", { className: layoutPositionClassName, "data-stid": "typeahead-trigger", style: triggerCompStyle, children: [mounted && (jsx(EGDSFakeInput, { "aria-label": [a11yLabel, searchTerm].filter((str) => str && str.length > 0).join(' '), "aria-expanded": dialog, icon: icon, label: label, hasClearButton: hasClearButton, handleClear: this.handleClear, "aria-describedby": invalid && stid ? `${stid}-input-error` : undefined, "data-stid": stid && triggerType === TypeaheadRenderType.MENU
                        ? `${stid}-menu-trigger`
                        : stid && triggerType === 'dialog'
                            ? `${stid}-dialog-trigger`
                            : '', domRef: this.typeaheadInputRef, onClick: () => {
                        this.openTypeahead(triggerType);
                    }, onKeyPress: (e) => {
                        this.handleKeyPress(e, triggerType);
                    }, onClickCapture: analytics && analyticsTracker.bind(this, analytics, ''), placeholder: placeholder, value: isMulti && Array.isArray(value) ? value.join(', ') : value, invalid: invalid, required: required, clearButtonLabel: searchTerm })), jsx(TriggerTag, { className: getClassNameList({
                        'is-hidden': mounted,
                    }), icon: icon, label: label, "data-stid": stid && `${stid}-input`, domRef: this.typeaheadInputRef, hasClearButton: false, hasVisibleLabel: hasVisibleLabel, name: name, onChange: Typeahead.defaultProps.onInputChange, placeholder: placeholder, value: searchTerm, id: stid && `${stid}-input`, required: required, clearButtonLabel: searchTerm })] }));
    }
    renderMenuTypeahead() {
        const { renderMenu, customTrigger } = this.props;
        const { dialog } = this.state;
        const showTypeahead = (customTrigger && customTrigger.showTypeahead) || dialog;
        return renderMenu ? (renderMenu(this.renderMenu())) : (jsxs(Fragment, { children: [jsx("div", { className: getClassNameList('uitk-typeahead-menu-layer', {
                        'uitk-typeahead-menu-layer-responsive': showTypeahead,
                        'is-hidden': !showTypeahead,
                    }), children: this.renderMenu() }), this.renderTrigger(TypeaheadRenderType.MENU)] }));
    }
    renderDialogTypeahead() {
        const { renderDialog, customTrigger } = this.props;
        const { dialog } = this.state;
        const showTypeahead = (customTrigger && customTrigger.showTypeahead) || dialog;
        return (jsxs(Fragment, { children: [renderDialog ? (dialog ? (renderDialog(this.renderDialog())) : null) : (jsx("div", { className: getClassNameList('uitk-typeahead-dialog-layer', {
                        'uitk-typeahead-dialog-layer-responsive': showTypeahead,
                        'is-hidden': !showTypeahead,
                    }), children: this.renderDialog() })), this.renderTrigger(TypeaheadRenderType.DIALOG)] }));
    }
    render() {
        const { renderType, stid, isMulti, value, onItemRemove, clearAllText, showPills, label, className } = this.props;
        const otherClassNames = this.filterLayoutPositionClassName(className, false) || [];
        const finalClassName = [getClassNameList('uitk-typeahead')?.split(' '), ...otherClassNames].join(' ');
        return (jsxs("div", { className: finalClassName, "data-stid": stid, children: [renderType === TypeaheadRenderType.DIALOG ? (this.renderDialogTypeahead()) : renderType === TypeaheadRenderType.MENU ? (this.renderMenuTypeahead()) : renderType === TypeaheadRenderType.ADAPTIVE ? (jsxs(Viewport, { children: [jsx(ViewSmall, { children: this.renderDialogTypeahead() }), jsx(ViewLarge, { children: this.renderMenuTypeahead() })] })) : (''), isMulti && showPills ? (jsx(EGDSTypeaheadSelections, { typeaheadLabel: label, hideHeading: true, values: value, stid: stid, onItemRemove: onItemRemove, onClear: this.handleClear, clearAllText: clearAllText, className: getClassNameList('uitk-spacing-margin-blockstart-three') })) : null] }));
    }
}
const EGDSTypeahead = React.forwardRef((props, ref) => {
    const l10n = useLocalization();
    return jsx(Typeahead, { l10n: l10n, ref: ref, ...props });
});

// Add client side typeahead experiment info to the below enum for logging purpose
var Experiment;
(function (Experiment) {
    Experiment[Experiment["EGTA_BEX_ML_V0"] = 36094] = "EGTA_BEX_ML_V0";
    Experiment[Experiment["EGTA_uTA_Demo"] = 32216] = "EGTA_uTA_Demo";
    Experiment[Experiment["ESS_Two_Line_Display"] = 33686] = "ESS_Two_Line_Display";
    Experiment[Experiment["PWA_ESS_Feedback_Loop_Enable"] = 31052] = "PWA_ESS_Feedback_Loop_Enable";
    Experiment[Experiment["EGTA_Rollout_Feature_Gate_for_Hotel_Property_Name_Filter"] = 37339] = "EGTA_Rollout_Feature_Gate_for_Hotel_Property_Name_Filter";
    Experiment[Experiment["BEX_Driveable_destination_Web"] = 41955] = "BEX_Driveable_destination_Web";
})(Experiment || (Experiment = {}));
class Experiments {
    static experimentMap = {};
    static init(experiments) {
        if (experiments) {
            Object.keys(Experiment).forEach((name) => {
                const value = experiments[name];
                let bucket;
                if (typeof value === 'number') {
                    // Blossom-flex
                    bucket = value;
                }
                else if (value && typeof value.bucket === 'number') {
                    // Shopping-pwa
                    bucket = value.bucket;
                }
                if (bucket !== undefined) {
                    const id = Experiment[name];
                    this.experimentMap[id] = { id, name, bucket };
                }
            });
        }
    }
    static isBucketed(experiment, bucket = 1) {
        return experiment in this.experimentMap && bucket === this.experimentMap[experiment].bucket;
    }
    static getClientVariants() {
        const variants = [];
        Object.keys(this.experimentMap).forEach((key) => {
            variants.push(`${key}.${this.experimentMap[key].bucket}`);
        });
        return variants.join('|');
    }
}

class InteractionMetrics {
    static getSuggestionTypes(s) {
        if (s && s.length > 0) {
            return s.map((item) => (item.category === 'GOOGLEMAPS' ? 'GPA' : item.type)).join(',');
        }
        return undefined;
    }
    static getSuggestionIds(s) {
        if (s && s.length > 0) {
            return s
                .map((item) => {
                if (item.category === 'GOOGLEMAPS') {
                    return `${item.sourceName}:${item.sourceId}`;
                }
                return item.essId ? `${item.essId.sourceName}:${item.essId.sourceId}` : '-:-';
            })
                .join(',');
        }
        return undefined;
    }
    mostRecentSelectedSuggestion;
    response;
    query;
    prefill;
    interactionStart;
    selectionTypeaheadEventMessage;
    constructor() {
        this.query = '';
        this.response = undefined;
        this.mostRecentSelectedSuggestion = undefined;
    }
    logTypeaheadOpen(prefill) {
        this.interactionStart = Date.now();
        this.prefill = prefill;
    }
    logTypeaheadRequest(query) {
        this.query = query || '';
        this.mostRecentSelectedSuggestion = undefined;
        this.response = undefined;
    }
    logSuggestionShown(response) {
        this.response = response;
    }
    logTypeaheadSelection(selection) {
        this.mostRecentSelectedSuggestion = this.response.sr[selection.index - 1];
    }
    logTypeaheadEventMessage(m) {
        this.selectionTypeaheadEventMessage = m;
    }
    getMetrics(metadata) {
        const userTypedQuery = this.query === this.prefill ? '' : this.query;
        const isAddrAutocomplete = this.response && this.response.sr && this.response.sr.length > 0 && this.response.sr[0].category === 'GOOGLEMAPS';
        const deviceType = metadata.getDevice && typeof metadata.getDevice === 'function'
            ? metadata.getDevice(window.navigator.useragent)
            : undefined;
        const metric = {
            contentFlavor: 'uitk',
            deviceType,
            inputLength: userTypedQuery ? userTypedQuery.length : 0,
            interactionTime: this.interactionStart ? Date.now() - this.interactionStart : undefined,
            isAddrAutocomplete,
            lob: metadata.lob,
            locale: metadata.locale,
            pageId: metadata.client,
            pointOfSale: window.location.hostname,
            selectionDepth: this.getSelectionDepth(),
            siteId: `${metadata.siteId}`,
            suggestionIds: this.response && InteractionMetrics.getSuggestionIds(this.response.sr),
            suggestionSelected: !!this.mostRecentSelectedSuggestion,
            suggestionShown: !!(this.response && this.response.sr && this.response.sr.length > 0),
            suggestionTypes: this.response && InteractionMetrics.getSuggestionTypes(this.response.sr),
            textInputElementId: metadata.inputId,
            timestamp: Date.now(),
            type: 'TypeaheadInteraction',
            userAgent: window.navigator.userAgent,
            userTypedQuery,
            variants: [this.response && this.response.activeABTests, Experiments.getClientVariants()]
                .filter(Boolean)
                .join('|'),
            zeroResults: this.response && this.response.sr && this.response.sr.length === 0,
            ...this.selectionTypeaheadEventMessage,
        };
        return metric;
    }
    getSelectionDepth() {
        if (this.mostRecentSelectedSuggestion && typeof this.mostRecentSelectedSuggestion.index !== 'undefined') {
            return (parseInt(this.mostRecentSelectedSuggestion.index, 10) + 1).toString();
        }
        return 'undefined';
    }
}

class PerformanceMetrics {
    static isStatusCodeInRange(statusCode, begin, end) {
        if (isNaN(statusCode)) {
            return false;
        }
        return begin <= statusCode && statusCode <= end;
    }
    entries = [];
    MAX_TRIGGER_COUNT_FOR_AGGREGATED_LOGGING = 10;
    essErrorCount = 0;
    TRIGGER_TYPE_FOCUS = 'focus';
    TRIGGER_TYPE_INPUT = 'input';
    errorCodes = [];
    proxyRegion = '';
    logTypeaheadRequest(query) {
        const triggerType = query.trim().length === 0 ? this.TRIGGER_TYPE_FOCUS : this.TRIGGER_TYPE_INPUT;
        const request = {
            query,
            timestamp: Date.now(),
            triggerType,
        };
        this.entries.push({
            request,
            responseShown: false,
        });
    }
    logResponse(response) {
        this.proxyRegion = response?.headers?.get('x-proxy-region') || '';
    }
    logTypeaheadResponse(response) {
        if (response) {
            const query = response.q;
            const suggestionCount = response.sr ? response.sr.length : 0;
            const entry = this.getRequestMetricEntry(query);
            if (entry) {
                const responseTimestamp = Date.now();
                entry.response = {
                    latency: responseTimestamp - entry.request.timestamp,
                    query,
                    suggestionCount,
                    timestamp: responseTimestamp,
                };
            }
        }
    }
    logSuggestionShown(query) {
        const entry = this.getResponseMetricEntry(query);
        if (entry) {
            entry.responseShown = true;
        }
    }
    logError(statusCode) {
        if (!PerformanceMetrics.isStatusCodeInRange(statusCode, 200, 299)) {
            // eslint-disable-next-line no-plusplus
            this.essErrorCount++;
        }
        this.errorCodes.push(statusCode);
    }
    getMetrics() {
        if (this.entries.length) {
            const metric = {};
            const entryCount = this.MAX_TRIGGER_COUNT_FOR_AGGREGATED_LOGGING < this.entries.length
                ? this.MAX_TRIGGER_COUNT_FOR_AGGREGATED_LOGGING
                : this.entries.length;
            for (let i = 0; i < entryCount; i++) {
                const entry = this.entries[i];
                if (entry.response) {
                    // @ts-ignore
                    metric[`latency${i}`] = entry.response.latency;
                    if (i === 0) {
                        metric.query0 = entry.response.query;
                    }
                }
            }
            metric.latencyMedian = this.getMedianLatency();
            const worstLatencyMetric = this.getWorstLatencyMetric();
            metric.queryWorst = worstLatencyMetric
                ? worstLatencyMetric.response && worstLatencyMetric.response.query
                : undefined;
            metric.latencyWorst = worstLatencyMetric
                ? worstLatencyMetric.response && worstLatencyMetric.response.latency
                : undefined;
            const last = this.entries[this.entries.length - 1];
            metric.queryLast = last.request.query;
            metric.latencyLast = last.response ? last.response.latency : undefined;
            metric.totalTriggers = this.entries.length;
            metric.totalSuccess = this.getSuccessMetrics().length;
            metric.totalOnePlusCharTypeaheadShown = this.entries.filter((entry) => entry.request.query && entry.responseShown).length;
            metric.onFocusTypeaheadShown = this.getResponseMetricEntry('')?.responseShown || false;
            if (this.essErrorCount > 0) {
                metric.essErrorCount = this.essErrorCount;
            }
            if (this.errorCodes.length > 0) {
                metric.error = this.errorCodes.join(',');
            }
            metric.proxyRegion = this.proxyRegion;
            return metric;
        }
        return undefined;
    }
    getRequestMetricEntry(query) {
        for (const entry of this.entries) {
            if (entry.request.query === query && !entry.response) {
                return entry;
            }
        }
        return undefined;
    }
    getResponseMetricEntry(query) {
        for (const entry of this.entries) {
            if (entry.request.query === query && entry.response) {
                return entry;
            }
        }
        return undefined;
    }
    getWorstLatencyMetric() {
        let worstLatencyMetric;
        for (const entry of this.entries) {
            if (entry.response) {
                if (!worstLatencyMetric ||
                    (worstLatencyMetric.response && worstLatencyMetric.response.latency < entry.response.latency)) {
                    worstLatencyMetric = entry;
                }
            }
        }
        return worstLatencyMetric;
    }
    latencySortFunction(a, b) {
        if (a.response && b.response) {
            return a.response.latency - b.response.latency;
        }
        if (a.response) {
            return -1;
        }
        return b.response ? 1 : 0;
    }
    getMedianLatency() {
        let medianLatency;
        const successMetrics = this.getSuccessMetrics();
        successMetrics.sort(this.latencySortFunction);
        if (successMetrics.length) {
            const mid = Math.floor(successMetrics.length / 2);
            // @ts-ignore
            const midLatency = successMetrics[mid].response.latency;
            if (successMetrics.length % 2) {
                medianLatency = midLatency;
            }
            else {
                // @ts-ignore
                const midMinusOneLatency = successMetrics[mid - 1].response.latency;
                medianLatency = (midMinusOneLatency + midLatency) / 2;
            }
        }
        return medianLatency;
    }
    getSuccessMetrics() {
        return this.entries.filter((entry) => entry.response);
    }
}

var SystemEventLevel;
(function (SystemEventLevel) {
    SystemEventLevel["CRITICAL"] = "CRITICAL";
    SystemEventLevel["ERROR"] = "ERROR";
    SystemEventLevel["WARN"] = "WARN";
    SystemEventLevel["INFO"] = "INFO";
    SystemEventLevel["UNKNOWN"] = "UNKNOWN";
})(SystemEventLevel || (SystemEventLevel = {}));
class SystemEvent {
    level;
    name;
    /**
     * Construct a new SystemEvent. This is a container for data marking a specific
     * event in your app. Ideally the name follows the enum pattern - all uppercase,
     * spaces replaced with underscores i.e. 'FETCH_ERROR'
     */
    constructor(level, name) {
        this.level = level;
        this.name = name;
    }
    /**
     * Get a string value of this event safe for Splunk.
     */
    toString() {
        return `SystemEvent(level=${this.level.toString()} name=${this.name})`;
    }
}

class ClientMetricsLogger {
    clientLogger;
    static pm;
    static im;
    static metadata;
    constructor(clientLogger) {
        this.clientLogger = clientLogger;
        if (!ClientMetricsLogger.pm || !ClientMetricsLogger.im) {
            this.initMetrics();
        }
    }
    initLogger(metadata) {
        const { client, lob, locale, siteId, personalize, inputId, getDevice, variants } = metadata;
        ClientMetricsLogger.metadata = { client, lob, locale, siteId, personalize, inputId, getDevice, variants };
    }
    logTypeaheadOpen(prefill) {
        ClientMetricsLogger.im.logTypeaheadOpen(prefill);
    }
    logTypeaheadRequest(query) {
        ClientMetricsLogger.im.logTypeaheadRequest(query);
        ClientMetricsLogger.pm.logTypeaheadRequest(query);
    }
    logResponse(response) {
        ClientMetricsLogger.pm.logResponse(response);
    }
    logTypeaheadResponse(response) {
        ClientMetricsLogger.pm.logTypeaheadResponse(response);
    }
    logSuggestionShown(response) {
        ClientMetricsLogger.pm.logSuggestionShown(response.q);
        ClientMetricsLogger.im.logSuggestionShown(response);
    }
    logSuggestionSelected(selected, taEventMessage) {
        ClientMetricsLogger.im.logTypeaheadSelection(selected);
        ClientMetricsLogger.im.logTypeaheadEventMessage(taEventMessage);
    }
    logError(statusCode) {
        ClientMetricsLogger.pm.logError(statusCode);
    }
    logTypeaheadClientMetrics() {
        if (this.clientLogger && typeof this.clientLogger.getLoggerWithIdentifier === 'function') {
            const logger = this.clientLogger.getLoggerWithIdentifier('uitk-react-typeahead');
            if (logger && typeof logger.logEvent === 'function') {
                logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'TypeaheadInteraction'), {
                    ...ClientMetricsLogger.im.getMetrics(ClientMetricsLogger.metadata),
                    ...ClientMetricsLogger.pm.getMetrics(),
                });
            }
        }
        this.initMetrics();
    }
    initMetrics() {
        ClientMetricsLogger.im = new InteractionMetrics(); // Clear interaction logs
        ClientMetricsLogger.pm = new PerformanceMetrics(); // Clear performance logs
    }
}

var EventName$1;
(function (EventName) {
    EventName["CLICK"] = "recommendation_module.selected";
    EventName["SEEN"] = "recommendation_module.presented";
})(EventName$1 || (EventName$1 = {}));
var EventType$1;
(function (EventType) {
    EventType["SEEN"] = "Impression";
    EventType["CLICK"] = "Interaction";
})(EventType$1 || (EventType$1 = {}));
const COMPONENT = 'destination-typeahead-input';
const SCHEMA_NAME = 'allRecommendations';
const RECOMMENDATION_TITLE_DEFAULT = {
    ALTERNATE: 'People also searched',
    POPULAR: 'Popular destinations',
};
const RECOMMENDATION_CATEGORIES = ['POPULAR', 'ALTERNATE'];
class RecommendationEvent {
    static getRecommendationSeenMessage(response, elementName) {
        if (response && response.sr && response.sr.length > 0) {
            const recommendedSuggestions = response.sr.filter((sr) => RECOMMENDATION_CATEGORIES.includes(sr.category));
            if (recommendedSuggestions && recommendedSuggestions.length > 0) {
                const recommendationEvent = {
                    eventType: EventType$1.SEEN,
                    eventName: EventName$1.SEEN,
                    egRecommendationResponseId: this.getEgRecommendationResponseId(response.recId),
                    recommendationTitle: this.getRecommendationTitle(recommendedSuggestions[0].category),
                    component: COMPONENT,
                    componentElement: elementName,
                    recommendations: this.getRecommendations(recommendedSuggestions),
                };
                return {
                    schemaName: SCHEMA_NAME,
                    messageContent: recommendationEvent,
                };
            }
        }
        return undefined;
    }
    static getRecommendationClickedMessage(selected, response, elementName) {
        if (selected && RECOMMENDATION_CATEGORIES.includes(selected.category)) {
            let rank = selected.index;
            if (response && response.sr && response.sr.length > 0) {
                const recommendations = response.sr.filter((sr) => sr.category === selected.category);
                for (let i = 0; i < recommendations.length; i++) {
                    if (recommendations[i].essId?.sourceId === selected.essId?.sourceId) {
                        rank = i;
                        break;
                    }
                }
            }
            const recommendationEvent = {
                eventType: EventType$1.CLICK,
                eventName: EventName$1.CLICK,
                egRecommendationResponseId: this.getEgRecommendationResponseId(),
                recommendationTitle: this.getRecommendationTitle(selected.category),
                component: COMPONENT,
                componentElement: elementName,
                recommendations: this.getSelectedRecommendations(selected, rank),
            };
            return {
                schemaName: SCHEMA_NAME,
                messageContent: recommendationEvent,
            };
        }
        return undefined;
    }
    static recommendationTitle;
    static egRecommendationResponseId;
    static getEgRecommendationResponseId(responseId) {
        if (responseId) {
            this.egRecommendationResponseId = responseId;
        }
        return this.egRecommendationResponseId;
    }
    static getRecommendationTitle(recommendationType) {
        if (!this.recommendationTitle) {
            this.recommendationTitle =
                RECOMMENDATION_TITLE_DEFAULT[recommendationType];
        }
        return this.recommendationTitle;
    }
    static getRecommendations(results) {
        const recommendations = [];
        let trueIndex = 0;
        if (results && results.length > 0) {
            results.forEach((resultItem) => {
                recommendations.push(this.getRecommendation(resultItem, trueIndex));
                // eslint-disable-next-line no-plusplus
                trueIndex++;
            });
            recommendations.push();
        }
        return recommendations;
    }
    static getSelectedRecommendations(result, rank) {
        const recommendations = [];
        if (result) {
            recommendations.push(this.getRecommendation(result, rank));
        }
        return recommendations;
    }
    static getRecommendation(resultItem, rank) {
        return {
            recommendationId: resultItem.essId?.sourceId,
            rank,
            presentationDetails: {
                name: resultItem.regionNames.primaryDisplayName,
            },
        };
    }
}

/* eslint-disable no-param-reassign */
class Utils {
    static UNKNOWN = 'UNKNOWN';
    static init(clientLogger) {
        if (clientLogger && typeof clientLogger.getLoggerWithIdentifier === 'function') {
            const logger = clientLogger.getLoggerWithIdentifier('uitk-react-typeahead');
            if (logger && typeof logger.logEvent === 'function') {
                Utils.logger = logger;
            }
        }
    }
    static isOrigin(domElementId) {
        domElementId = domElementId.toLowerCase();
        return (domElementId.indexOf('origin') > -1 ||
            domElementId.indexOf('from') > -1 ||
            domElementId.indexOf('departure') > -1 ||
            domElementId.indexOf('drop') > -1 ||
            domElementId.indexOf('home') > -1 ||
            domElementId.indexOf('loc2') > -1);
    }
    static isDestination(domElementId) {
        domElementId = domElementId.toLowerCase();
        return (domElementId.indexOf('dest') > -1 ||
            domElementId.indexOf('to') > -1 ||
            domElementId.indexOf('arrival') > -1 ||
            domElementId.indexOf('near') > -1 ||
            domElementId.indexOf('pick') > -1 ||
            domElementId.indexOf('location') === 0);
    }
    static getDomElement() {
        // Class name used in homepage and SRP typeahead component
        const element = document
            .querySelectorAll('.uitk-typeahead-dialog-layer-responsive .uitk-typeahead-dialog, .uitk-typeahead-menu-layer-responsive .uitk-typeahead-menu-container')[0]
            ?.getElementsByTagName('input')[0];
        if (!element) {
            Utils.logError({ message: 'Failed to retrieve data for the typeahead UI component' });
        }
        return element;
    }
    static logError(error) {
        try {
            if (Utils.logger) {
                error = error.stack ? { message: error.message, stack: error.stack } : error;
                Utils.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'TypeaheadLoggingError'), error);
            }
        }
        catch (e) {
            /* istanbul ignore next */
            // tslint:disable-next-line:no-console
            console.error(`unable to log errors from typeahead-client-logger. Reason:${e}`);
        }
    }
    static getInputElementId(inputEl) {
        let id;
        if (inputEl) {
            id = inputEl.getAttribute('data-stid');
            if (!id) {
                Utils.logError({ message: "Failed to retrieve input element id from 'data-stid'" });
            }
        }
        return id || Utils.UNKNOWN;
    }
    static getSubLob(lob, sublob) {
        if (!sublob) {
            const usp = new URLSearchParams(window.location.search);
            sublob = usp.get('pwaSubLOB') || '';
        }
        switch (lob.toLowerCase()) {
            case 'flights':
                switch (sublob && sublob.toLowerCase()) {
                    case 'oneway':
                        return 'OW';
                    case 'multicity':
                        return 'MD';
                    default:
                        return 'RT';
                }
            case 'packages':
                switch (sublob && sublob.toLowerCase()) {
                    case 'fhc':
                        return 'FHC';
                    case 'hc':
                        return 'HC';
                    case 'fc':
                        return 'FC';
                    default:
                        return 'FH';
                }
            default:
                return '';
        }
    }
    static logger;
}

var EventName;
(function (EventName) {
    EventName["FOCUS"] = "typeaheadFocused";
    EventName["BLUR"] = "typeaheadBlurred";
    EventName["SELECT"] = "typeaheadSuggestionSelected";
    EventName["CURRENT_LOCATION"] = "currentLocation";
})(EventName || (EventName = {}));
var EventType;
(function (EventType) {
    EventType["TYPEAHEAD_INTERACTION"] = "typeaheadInteraction";
})(EventType || (EventType = {}));
var SearchField;
(function (SearchField) {
    SearchField["O"] = "Origin";
    SearchField["D"] = "Destination";
    SearchField["U"] = "UNKNOWN";
})(SearchField || (SearchField = {}));
var SourceSystem;
(function (SourceSystem) {
    SourceSystem["GAIA"] = "GAIA";
    SourceSystem["LCM"] = "LCM";
    SourceSystem["GOOGLE"] = "GOOGLE";
    SourceSystem["UNKNOWN"] = "UNKNOWN";
})(SourceSystem || (SourceSystem = {}));
var SuggestionType;
(function (SuggestionType) {
    SuggestionType["ALTERNATE"] = "Alternate";
    SuggestionType["CURRENT_LOCATION"] = "CurrentLocation";
    SuggestionType["POPULAR"] = "Popular";
    SuggestionType["GOOGLE"] = "Google";
    SuggestionType["NORMAL"] = "Normal";
    SuggestionType["RECENT_HISTORY"] = "RecentHistory";
})(SuggestionType || (SuggestionType = {}));
// Tslint:disable-next-line:max-line-length
// UIS Prime Schema - https://github.expedia.biz/Brand-Expedia/uis-prime-schemas/blob/master/schemas/micro/expediaSuggestedSearch/typeahead.json
class TypeaheadEvent {
    typeaheadEvent;
    constructor(eventName, session) {
        const isCurrLoc = EventName.CURRENT_LOCATION === eventName;
        if (isCurrLoc) {
            eventName = EventName.SELECT;
        }
        if (EventName.FOCUS === eventName) {
            this.typeaheadEvent = {
                event: {
                    name: eventName,
                    type: EventType.TYPEAHEAD_INTERACTION,
                },
                numberOfBackspaces: session.noOfBackspaces,
                page: {
                    actionLocation: session.fieldName,
                    lineOfBusiness: session.metadata.lob + this.getSublob(session.metadata.lob, session.metadata.subLob),
                    pageName: this.getPageName(session.metadata.client || '', session.href),
                },
                searchField: this.getSearchField(session.fieldName),
                searchFieldContent: session.input?.value || '',
                timestamps: {
                    receivedAt: Date.now(),
                },
            };
        }
        else {
            this.typeaheadEvent = {
                event: {
                    name: eventName,
                    type: EventType.TYPEAHEAD_INTERACTION,
                },
                numberOfBackspaces: session.noOfBackspaces,
                page: {
                    actionLocation: session.fieldName,
                    lineOfBusiness: session.metadata.lob + this.getSublob(session.metadata.lob, session.metadata.subLob),
                    pageName: this.getPageName(session.metadata.client || '', session.href),
                },
                searchField: this.getSearchField(session.fieldName),
                searchFieldContent: session.query === session.prefill ? '' : session.query || '',
                timestamps: {
                    receivedAt: Date.now(),
                },
            };
        }
        if (EventName.FOCUS === eventName) {
            // We are setting the initial time stamp when user first interacts with typeahead input box
            session.isResolve = false;
            session.focusTime = new Date().toString();
        }
        if ([EventName.SELECT, EventName.BLUR].indexOf(eventName) > -1) {
            if (session.response) {
                this.setSuggestionShownFields(session.response);
            }
            if (session.selected) {
                if (EventName.BLUR === eventName) {
                    this.typeaheadEvent.typeaheadSelection = true;
                    this.typeaheadEvent.charactersTyped = this.typeaheadEvent.searchFieldContent.length;
                    this.calculateTimeSpendOnTypeaheadInput(session);
                }
                this.setSuggestionSelectedFields(session.selected);
            }
            if (session.selected === undefined && EventName.BLUR === eventName) {
                this.calculateTimeSpendOnTypeaheadInput(session);
                this.typeaheadEvent.typeaheadSelection = false;
                this.typeaheadEvent.charactersTyped = this.typeaheadEvent.searchFieldContent.length;
                const input = document.getElementsByName('regionId')?.item(0);
                const regionId = input?.value || 'UNKNOWN';
                this.typeaheadEvent.searchDestination = {
                    id: session.isResolve ? 'UNKNOWN' : regionId,
                    sourceSystem: 'UNKNOWN',
                    fullName: session.isResolve ? session.query : session.prefill || '',
                };
            }
            if (isCurrLoc) {
                this.setCurrentLocationSelectedFields();
            }
        }
    }
    getTypeaheadEventMessage() {
        return {
            messageContent: this.typeaheadEvent,
            schemaName: 'typeahead',
        };
    }
    getFlattenedJsonMessage() {
        return {
            'ta.page.actionLocation': this.typeaheadEvent.page.actionLocation,
            'ta.page.lob': this.typeaheadEvent.page.lineOfBusiness,
            'ta.page.name': this.typeaheadEvent.page.pageName,
            'ta.searchField': this.typeaheadEvent.searchField,
            'ta.searchFieldContent': this.typeaheadEvent.searchFieldContent,
            'ta.timestamps.receivedAt': this.typeaheadEvent.timestamps.receivedAt,
            'ta.noOfSuggestions': this.typeaheadEvent.noOfSuggestions,
            'ta.requestId': this.typeaheadEvent.serviceRequestLogId,
            'ta.suggestionType': this.typeaheadEvent.suggestionType,
            'ta.searchDestination': `${this.typeaheadEvent.searchDestination?.sourceSystem}:${this.typeaheadEvent.searchDestination?.id}`,
            'ta.selectionDepth': this.typeaheadEvent.selectionDepth,
            'ta.suggestionList': this.typeaheadEvent.suggestionList?.map((s) => `${s.sourceSystem}:${s.id}`).join(','),
        };
    }
    setSuggestionShownFields(response) {
        this.typeaheadEvent.noOfSuggestions = response.sr.length;
        this.typeaheadEvent.serviceRequestLogId = response.rid;
        if (response.sr && response.sr.length) {
            this.typeaheadEvent.suggestionType = this.getSuggestionType(response.sr[0].category);
            this.typeaheadEvent.suggestionList = this.getSuggestionList(response.sr);
        }
    }
    setSuggestionSelectedFields(selected) {
        this.typeaheadEvent.suggestionType = this.getSuggestionType(selected.category);
        this.typeaheadEvent.searchDestination = this.getGeoEntity(selected);
        this.typeaheadEvent.selectionDepth = parseInt(selected.index, 10);
        if (selected && selected.displayType !== 'CATEGORIZED')
            this.typeaheadEvent.selectionDepth += 1;
    }
    setCurrentLocationSelectedFields() {
        this.typeaheadEvent.suggestionType = SuggestionType.CURRENT_LOCATION;
        this.typeaheadEvent.typeaheadSelection = true;
        this.typeaheadEvent.searchDestination = {
            fullName: 'CurrentLocation',
            id: '',
            type: SuggestionType.CURRENT_LOCATION,
        };
        this.typeaheadEvent.selectionDepth = 1;
    }
    getSearchField(domElementId) {
        if (!domElementId) {
            return SearchField.U;
        }
        if (Utils.isOrigin(domElementId)) {
            return SearchField.O;
        }
        return Utils.isDestination(domElementId) ? SearchField.D : SearchField.U;
    }
    getGeoEntity(selected) {
        return {
            fullName: selected.regionNames.lastSearchName,
            id: this.getSourceId(selected),
            shortName: selected.regionNames.shortName,
            sourceSystem: this.getSourceSystem(selected),
            type: selected.type,
        };
    }
    getSourceId(s) {
        return s.sourceId || s.essId?.sourceId || s.hotelId || s.gaiaId || Utils.UNKNOWN;
    }
    getSourceSystem(s) {
        let sourceName;
        if (s.hotelId) {
            sourceName = s.sourceName || s.essId?.sourceName || 'LCM';
        }
        else {
            sourceName = s.sourceName || s.essId?.sourceName || s.gaiaId ? 'GAI' : '';
        }
        switch (sourceName) {
            case 'GAI':
                return SourceSystem.GAIA;
            case 'LCM':
                return SourceSystem.LCM;
            case 'GPA':
                return SourceSystem.GOOGLE;
            default:
                return sourceName || SourceSystem.UNKNOWN;
        }
    }
    getSuggestionType(category) {
        switch (category) {
            case 'GOOGLEMAPS':
                return SuggestionType.GOOGLE;
            case 'USERHISTORY':
                return SuggestionType.RECENT_HISTORY;
            case 'ALTERNATE':
                return SuggestionType.ALTERNATE;
            case 'POPULAR':
                return SuggestionType.POPULAR;
            case 'CURRENTLOCATION':
                return SuggestionType.CURRENT_LOCATION;
            default:
                return SuggestionType.NORMAL;
        }
    }
    getSuggestionList(suggestions) {
        return suggestions.map((s) => {
            const suggId = {
                id: this.getSourceId(s),
                sourceSystem: this.getSourceSystem(s),
                type: s.type,
            };
            if (s.hierarchyInfo && s.hierarchyInfo.relation && s.hierarchyInfo.relation.length) {
                suggId.reason = s.hierarchyInfo.relation[0];
            }
            return suggId;
        });
    }
    getSublob(lob, sublob) {
        sublob = Utils.getSubLob(lob, sublob);
        return sublob ? `.${sublob}` : '';
    }
    getPageName(client, href) {
        /* Cases:
                Case 1: If Client = Homepage,
                        then check for Href because currently we are getting Client=Homepage for landing page as well
                Case 2: If Client is not Homepage
                        then we first deduce the page name with client if found UNKNOWN then we use Href
                Case 3: If Client is not present,
                        then check the Href if it belongs to HomePage then resolve otherwise check for other possibility
                Case 4: If client is not Homepage but the href is of Homepage(Special Case)
             */
        href = href.toLowerCase();
        client = client.toLowerCase();
        if (client && !(client.indexOf('homepage') !== -1)) {
            const pageName = this.checkIfHomepage(href) ? 'Homepage' : this.findPageName(client);
            if (pageName.indexOf('UNKNOWN') !== -1) {
                return this.findPageName(href);
            }
            return pageName;
        }
        return this.checkIfHomepage(href) ? 'Homepage' : this.findPageName(href);
    }
    checkIfHomepage(href) {
        // RegExp for homePage
        // Match with https://www.expedia.co.in/ , https://www.travelocity.com/ and similar hrefs
        const regex = /^(https:\/\/)[a-zA-Z]{2,}\.[a-zA-Z0-9]{1,61}\.[a-zA-Z.]{2,}((\/\?)|\/$)/;
        return regex.test(href);
    }
    findPageName(pageName) {
        if (pageName.indexOf('package') !== -1 || pageName.indexOf('packagetype') !== -1) {
            return this.returnPageName('Packages', pageName);
        }
        if (pageName.indexOf('flight') !== -1) {
            return this.returnPageName('Flights', pageName);
        }
        if (pageName.indexOf('hotel') !== -1) {
            return this.returnPageName('Hotels', pageName);
        }
        if (pageName.indexOf('car') !== -1) {
            return this.returnPageName('Cars', pageName);
        }
        if (pageName.indexOf('lx') !== -1 ||
            pageName.indexOf('activities') !== -1 ||
            pageName.indexOf('things-to-do') !== -1) {
            return this.returnPageName('Activities', pageName);
        }
        if (pageName.indexOf('rail') !== -1) {
            return this.returnPageName('Rails', pageName);
        }
        return 'UNKNOWN';
    }
    returnPageName(name, pathName) {
        if (pathName.indexOf('search') !== -1) {
            return `${name}.Search`;
        }
        return `${name}LandingPage`;
    }
    /*
      This method is used to calculate the time spent by user on typeahead input box
      It first calculate the timestamp and store it in case of blur event only.
       */
    calculateTimeSpendOnTypeaheadInput(session) {
        // @ts-ignore
        const focus = session.focusTime;
        this.typeaheadEvent.timestamps.timeFromFocusToBlur = new Date().getTime() - new Date(focus).getTime();
    }
}

class Optional {
    static of(value) {
        if (Optional.isEmpty(value)) {
            throw new Error("Optional.of doesn't accept undefined or null values. Use Optional.ofNullable instead");
        }
        return new Optional(value);
    }
    static ofNullable(value) {
        return new Optional(value);
    }
    static empty() {
        // @ts-ignore
        return new Optional(null);
    }
    static isEmpty(value) {
        return value === null || value === undefined || typeof value === 'undefined';
    }
    value;
    constructor(value) {
        this.value = value;
    }
    get() {
        if (this.isPresent()) {
            return this.value;
        }
        throw new Error('Exception while trying to get the value of an empty Optional');
    }
    isPresent() {
        return !Optional.isEmpty(this.value);
    }
    isNotPresent() {
        return !this.isPresent();
    }
    orElse(alt) {
        if (this.isPresent()) {
            return this.value;
        }
        return alt;
    }
}

var LogType;
(function (LogType) {
    LogType["TYPEAHEAD_FOCUS"] = "typeahead-focus";
    LogType["TYPEAHEAD_SELECT"] = "typeahead-select";
    LogType["TYPEAHEAD_BLUR"] = "typeahead-blur";
    LogType["WIZARD_SUBMIT"] = "wizard-submit";
    LogType["RECOMMENDATION_SEEN"] = "recommendation-seen";
    LogType["RECOMMENDATION_SELECTED"] = "recommendation-selected";
})(LogType || (LogType = {}));
class TypeaheadEventLogger {
    analytics;
    static sessionStore;
    isEgtaLoggingEnabled = true;
    static leg = {};
    // 1 - Create logger
    constructor(analytics) {
        this.analytics = analytics;
        if (!TypeaheadEventLogger.sessionStore) {
            this.initSessionStore();
        }
    }
    // 2 - initialize logger.
    initLogger(metadata) {
        this.initSessionStore();
        this.initializeInputField();
        const { client, subLob, lob, locale, siteId, personalize, enableEgta } = metadata;
        TypeaheadEventLogger.sessionStore.metadata = { client, subLob, lob, locale, siteId, personalize, enableEgta };
    }
    logTypeaheadRequest(query) {
        if (TypeaheadEventLogger.sessionStore) {
            TypeaheadEventLogger.sessionStore.query = query;
        }
    }
    logTypeaheadResponse(response) {
        TypeaheadEventLogger.sessionStore.response = response;
        TypeaheadEventLogger.sessionStore.selected = undefined;
        const recEventMessage = RecommendationEvent.getRecommendationSeenMessage(response, TypeaheadEventLogger.sessionStore.fieldName || '');
        if (recEventMessage) {
            this.trackEventToUisPrime(this.getReferrerId(LogType.RECOMMENDATION_SEEN), LogType.RECOMMENDATION_SEEN, false, [
                recEventMessage,
            ]);
        }
    }
    logTypeaheadOpen(prefill) {
        TypeaheadEventLogger.sessionStore.prefill = prefill;
        this.initializeInputField();
        if (this.isEgtaLoggingEnabled) {
            this.trackEventToUisPrime(this.getReferrerId(LogType.TYPEAHEAD_FOCUS), LogType.TYPEAHEAD_FOCUS, false, [
                new TypeaheadEvent(EventName.FOCUS, TypeaheadEventLogger.sessionStore).getTypeaheadEventMessage(),
            ]);
        }
    }
    logSuggestionSelected(selected) {
        const isCurrLoc = selected?.type === 'CURRENT_LOCATION';
        if (Optional.ofNullable(selected).isPresent() && this.essResponseIsNotEmpty()) {
            TypeaheadEventLogger.sessionStore.selected = TypeaheadEventLogger.sessionStore.response.sr[selected.index - 1];
        }
        const eventName = isCurrLoc ? EventName.CURRENT_LOCATION : EventName.SELECT;
        const taEvent = new TypeaheadEvent(eventName, TypeaheadEventLogger.sessionStore);
        const recEventMessage = RecommendationEvent.getRecommendationClickedMessage(TypeaheadEventLogger.sessionStore.selected, TypeaheadEventLogger.sessionStore.response, TypeaheadEventLogger.sessionStore.fieldName || '');
        if (this.isEgtaLoggingEnabled) {
            const uisPrimeMessages = [taEvent.getTypeaheadEventMessage()];
            if (recEventMessage) {
                this.trackEventToUisPrime(this.getReferrerId(LogType.RECOMMENDATION_SELECTED), LogType.RECOMMENDATION_SELECTED, false, [recEventMessage]);
            }
            this.trackEventToUisPrime(this.getReferrerId(), LogType.TYPEAHEAD_SELECT, false, uisPrimeMessages);
        }
        return taEvent.getFlattenedJsonMessage();
    }
    /* The following two methods to be used by blossom-ui / buttercup / any other client who uses typeahead*/
    logTypeaheadBlurEvent() {
        const messages = this.buildUisPrimeMessages();
        if (this.isEgtaLoggingEnabled) {
            messages.push(new TypeaheadEvent(EventName.BLUR, TypeaheadEventLogger.sessionStore).getTypeaheadEventMessage());
        }
        this.trackEventToUisPrime(this.getReferrerId(), LogType.TYPEAHEAD_BLUR, false, messages);
        TypeaheadEventLogger.sessionStore.noOfBackspaces = 0;
        TypeaheadEventLogger.sessionStore.input?.removeEventListener('keydown', this.keyDown);
    }
    isResolveNoSuggestionSelected(flag) {
        TypeaheadEventLogger.sessionStore.isResolve = flag;
    }
    logWizardSubmitEvent() {
        // Disabling since it is not consistent and also to fix - https://jira.expedia.biz/browse/ECOMSVC-4216
        // this.trackEventToUisPrime(this.getReferrerId(), LogType.WIZARD_SUBMIT, true, this.buildUisPrimeMessages());
        this.resetLoggerState();
    }
    trackEventToUisPrime(rfrr, linkName, trackImmediate, messages) {
        try {
            this.analytics?.trackEvent(rfrr, linkName, this.analytics?.omnitureData, null, trackImmediate, messages);
        }
        catch (e) {
            Utils.logError({
                ...(typeof e === 'string' ? { e } : { ...e }),
                message: 'Failed typeahead event tracking to UIS Prime',
            });
        }
    }
    buildUisPrimeMessages() {
        if (Utils.isOrigin(TypeaheadEventLogger.sessionStore.fieldName || '')) {
            TypeaheadEventLogger.leg.origin = { ...TypeaheadEventLogger.sessionStore };
        }
        else {
            TypeaheadEventLogger.leg.dest = { ...TypeaheadEventLogger.sessionStore };
        }
        return [...this.getTrackingDataEvents(), this.getSelectedSuggestionEvents()];
    }
    getTrackingDataEvents() {
        const searchTerm = this.getUserTypedQuery(TypeaheadEventLogger.sessionStore);
        const numOfCharsTyped = searchTerm ? searchTerm.length : 0;
        const trackingData = [];
        /* To get the position of selected suggestion [event44] */
        if (this.isASuggestionSelected()) {
            trackingData.push(
            // @ts-ignore
            Object.freeze({
                messageContent: {
                    name: 'TypeaheadSelectionDepth',
                    value: this.getSelectedSuggestionPosition(),
                },
                schemaName: 'events',
            }));
        }
        /* To see if user interacted with typeahead or not [event45] */
        if (TypeaheadEventLogger.sessionStore.response) {
            trackingData.push(
            // @ts-ignore
            Object.freeze({
                messageContent: {
                    name: 'TypeaheadSearch',
                    value: 0,
                },
                schemaName: 'events',
            }));
        }
        /* To get the number of characters typed by the user [event46] */
        trackingData.push(
        // @ts-ignore
        Object.freeze({
            messageContent: {
                name: 'TypeaheadCharactersTyped',
                value: numOfCharsTyped,
            },
            schemaName: 'events',
        }));
        return trackingData;
    }
    getSelectedSuggestionEvents() {
        const originSuggestionData = this.getSelectedSuggestionData('origin', TypeaheadEventLogger.leg.origin);
        const destSuggestionData = this.getSelectedSuggestionData('dest', TypeaheadEventLogger.leg.dest);
        return Object.freeze({
            messageContent: {
                selectedSuggestions: [originSuggestionData, destSuggestionData],
            },
            schemaName: 'allSelectedSuggestions',
        });
    }
    getSelectedSuggestionData(searchField, session) {
        const suggestion = session?.selected;
        const alternateName = searchField === 'origin' && !session
            ? 'OriginNotApplicable'
            : session?.query || session?.prefill || 'TANoneSelected';
        const userTypedQuery = this.getUserTypedQuery(TypeaheadEventLogger.sessionStore);
        const lastSearchName = (suggestion && suggestion.regionNames && suggestion.regionNames.lastSearchName) || alternateName;
        if (suggestion) {
            if (suggestion.essId) {
                return {
                    essRequestId: (TypeaheadEventLogger.sessionStore.response || { rid: '-' }).rid,
                    lastSearchName,
                    relation: (suggestion &&
                        suggestion.hierarchyInfo &&
                        suggestion.hierarchyInfo.relation &&
                        suggestion.hierarchyInfo.relation[0]) ||
                        '-',
                    searchField,
                    sourceName: suggestion.essId.sourceName,
                    stringSourceId: ((suggestion && suggestion.essId && suggestion.essId.sourceId) || -1).toString(),
                    suggestionType: suggestion ? suggestion.type : 'UNKNOWN',
                    userHistory: suggestion ? suggestion.category === 'USERHISTORY' : false,
                    userTypedQuery,
                };
            }
            return {
                essRequestId: (TypeaheadEventLogger.sessionStore.response || { rid: '-' }).rid,
                lastSearchName,
                relation: (suggestion &&
                    suggestion.hierarchyInfo &&
                    suggestion.hierarchyInfo.relation &&
                    suggestion.hierarchyInfo.relation[0]) ||
                    '-',
                searchField,
                sourceName: suggestion.sourceName ? suggestion.sourceName : 'UNKNOWN',
                stringSourceId: ((suggestion && suggestion.essId && suggestion.essId.sourceId) || -1).toString(),
                suggestionType: suggestion ? suggestion.type : 'UNKNOWN',
                userHistory: suggestion ? suggestion.category === 'USERHISTORY' : false,
                userTypedQuery,
            };
        }
        return {
            essRequestId: (TypeaheadEventLogger.sessionStore.response || { rid: '-' }).rid,
            lastSearchName,
            relation: (suggestion &&
                suggestion.hierarchyInfo &&
                suggestion.hierarchyInfo.relation &&
                suggestion.hierarchyInfo.relation[0]) ||
                '-',
            searchField,
            sourceName: 'UNKNOWN',
            stringSourceId: ((suggestion && suggestion.essId && suggestion.essId.sourceId) || -1).toString(),
            suggestionType: suggestion ? suggestion.type : 'UNKNOWN',
            userHistory: suggestion ? suggestion.category === 'USERHISTORY' : false,
            userTypedQuery,
        };
    }
    getReferrerId(logType) {
        if (logType === LogType.RECOMMENDATION_SEEN) {
            return 'TYPEAHEAD.EGREC.RecSeen';
        }
        if (logType === LogType.RECOMMENDATION_SELECTED) {
            return 'TYPEAHEAD.EGREC.RecSelected';
        }
        const metadata = TypeaheadEventLogger.sessionStore.metadata || {};
        const parts = [];
        // @ts-ignore //
        parts.push(this.getLaunchView(metadata.client || ''));
        // @ts-ignore //
        parts.push(this.getWizard(metadata.client || ''));
        // @ts-ignore //
        parts.push(metadata.lob || 'Unknown');
        // @ts-ignore //
        parts.push(this.getShortLob(metadata.lob || '', metadata.subLob));
        // @ts-ignore //
        parts.push(metadata.locale || '-');
        // @ts-ignore //
        parts.push(metadata.siteId || '-');
        if (logType === LogType.TYPEAHEAD_FOCUS) {
            // @ts-ignore
            parts.push('TAStart');
        }
        else {
            // @ts-ignore
            parts.push(this.getTypeaheadTrigger());
            // @ts-ignore
            parts.push(...this.getInstrumentationValue((metadata.lob || '').toLowerCase()));
        }
        return parts.join('|');
    }
    getLaunchView(clientId) {
        // eslint-disable-next-line no-param-reassign
        clientId = clientId.toLowerCase();
        if (clientId.indexOf('home') > -1) {
            return 'Home';
        }
        if (clientId.indexOf('flight') > -1) {
            return 'FLT';
        }
        if (clientId.indexOf('hotels.search') > -1) {
            // For hotels search page bernie appends "HOT.SR" so removing here
            return '';
        }
        if (clientId.indexOf('hotel') > -1) {
            return 'HTL';
        }
        if (clientId.indexOf('vacationrental') > -1) {
            return 'VR';
        }
        if (clientId.indexOf('pack') > -1) {
            return 'PKG';
        }
        if (clientId.indexOf('car') > -1) {
            return 'CAR';
        }
        if (clientId.indexOf('cru') > -1) {
            return 'CRU';
        }
        if (clientId.indexOf('lx') > -1) {
            return 'LX';
        }
        if (clientId.indexOf('rail') > -1) {
            return 'RAIL';
        }
        if (clientId.indexOf('open') > -1) {
            return 'OPEN';
        }
        return clientId;
    }
    getWizard(client) {
        switch (client.toLowerCase()) {
            case 'hotels.search':
            case 'flights.search':
            case 'open.search':
            case 'cars.search':
            case 'lx.Search':
            case 'packages.search':
                return 'UpdateSearch';
            default:
                return 'CoreWzd';
        }
    }
    getShortLob(lob, subLob) {
        switch (lob.toLowerCase()) {
            case 'flights':
                return `F.${Utils.getSubLob(lob, subLob)}`;
            case 'hotels':
                return 'H';
            case 'packages':
                return Utils.getSubLob(lob, subLob);
            case 'activities':
                return 'LX';
            case 'cruises':
                return 'CR';
            case 'cars':
                return 'C';
            default:
                return '-';
        }
    }
    getTypeaheadTrigger() {
        if (!this.essResponseIsNotEmpty()) {
            return 'TANoShow.TAPrevSearch';
        }
        const historyResultsShown = TypeaheadEventLogger.sessionStore.response.q === '';
        if (historyResultsShown) {
            return this.isASuggestionSelected() ? 'TAShow.TAFocus' : 'TAShow.TANoSelection';
        }
        return this.isASuggestionSelected() ? 'TAShow.TASelection' : 'TAShow.TANoSelection';
    }
    getInstrumentationValue(lob) {
        const result = [];
        let index = '-';
        if (this.getSelectedSuggestionPosition() >= 0) {
            index = this.getSelectedSuggestionPosition().toString();
        }
        const length = this.essResponseIsNotEmpty() ? TypeaheadEventLogger.sessionStore.response.sr.length : 0;
        if (lob === 'flights' || lob === 'hotels') {
            result.push(`${this.getHierarchyType(TypeaheadEventLogger.sessionStore.selected)}#${index}`);
        }
        else {
            result.push(`L#${index}`);
        }
        result.push(`${this.getUserTypedQuery(TypeaheadEventLogger.sessionStore) &&
            TypeaheadEventLogger.sessionStore.metadata &&
            TypeaheadEventLogger.sessionStore.metadata.enableEgta
            ? 'EGTA'
            : 'ESS'}#${length}`);
        result.push(`UH#${this.getNumberOfUserHistorySuggestions()}`);
        return result;
    }
    getHierarchyType(suggestion) {
        if (Optional.ofNullable(suggestion).isPresent()) {
            if (this.isChild(suggestion)) {
                return 'CL';
            }
            if (this.isParent(suggestion)) {
                return 'PL';
            }
        }
        return 'DL';
    }
    isChild(suggestion) {
        if (Optional.ofNullable(suggestion).isNotPresent()) {
            return false;
        }
        const relation = (suggestion && suggestion.hierarchyInfo && suggestion.hierarchyInfo.relation) || [];
        if (relation.length === 0) {
            return false;
        }
        return relation.indexOf('child') >= 0;
    }
    isParent(suggestion) {
        if (Optional.ofNullable(suggestion).isNotPresent() || !this.essResponseIsNotEmpty()) {
            return false;
        }
        const index = parseInt(Optional.ofNullable(suggestion.index).orElse('-1'), 10);
        if (index < 0 || TypeaheadEventLogger.sessionStore.response.sr.length <= index + 1) {
            return false;
        }
        return this.isChild(TypeaheadEventLogger.sessionStore.response.sr[index + 1]);
    }
    getNumberOfUserHistorySuggestions() {
        if (TypeaheadEventLogger.sessionStore.metadata && !!TypeaheadEventLogger.sessionStore.metadata.personalize) {
            if (!this.essResponseIsNotEmpty()) {
                return '0';
            }
            const suggestions = TypeaheadEventLogger.sessionStore.response.sr;
            return suggestions.filter((suggestion) => suggestion.category === 'USERHISTORY').length.toString();
        }
        return '-';
    }
    /** ********* Util methods ************/
    essResponseIsNotEmpty() {
        return (TypeaheadEventLogger.sessionStore.response &&
            TypeaheadEventLogger.sessionStore.response.sr &&
            TypeaheadEventLogger.sessionStore.response.sr.length > 0);
    }
    isASuggestionSelected() {
        return !!TypeaheadEventLogger.sessionStore.selected;
    }
    getUserTypedQuery(session) {
        return session.query === session.prefill ? '' : session.query;
    }
    getSelectedSuggestionPosition() {
        if (!this.isASuggestionSelected() ||
            Optional.ofNullable(TypeaheadEventLogger.sessionStore.selected.index).isNotPresent()) {
            return -1;
        }
        try {
            return parseInt(TypeaheadEventLogger.sessionStore.selected.index, 10) + 1;
        }
        catch (e) {
            return -1;
        }
    }
    resetLoggerState() {
        this.initSessionStore();
        TypeaheadEventLogger.leg = {};
    }
    /* istanbul ignore next */
    keyDown(event) {
        if ((event.key && ['Delete', 'Backspace'].indexOf(event.key) > -1) ||
            (event.keyCode && [8, 46].indexOf(event.keyCode) > -1)) {
            TypeaheadEventLogger.sessionStore.noOfBackspaces += 1;
        }
    }
    initializeInputField() {
        TypeaheadEventLogger.sessionStore.input = Utils.getDomElement();
        TypeaheadEventLogger.sessionStore.fieldName = Utils.getInputElementId(TypeaheadEventLogger.sessionStore.input);
        TypeaheadEventLogger.sessionStore.input?.removeEventListener('keydown', this.keyDown);
        TypeaheadEventLogger.sessionStore.input?.addEventListener('keydown', this.keyDown);
    }
    initSessionStore() {
        TypeaheadEventLogger.sessionStore = {
            metadata: { lob: Utils.UNKNOWN },
            noOfBackspaces: 0,
            query: '',
            href: window.location.href,
        };
    }
}

class TypeaheadLogger {
    tel;
    cml;
    prevResponse;
    response;
    shownResponse;
    metadata;
    constructor(analytics, clientLogger, experiments) {
        try {
            Experiments.init(experiments);
            Utils.init(clientLogger);
            this.tel = new TypeaheadEventLogger(analytics);
            this.cml = new ClientMetricsLogger(clientLogger);
        }
        catch (e) {
            Utils.logError(e);
        }
    }
    initLogger(metadata) {
        this.metadata = metadata;
    }
    logTypeaheadOpen(prefill) {
        try {
            this.tel?.initLogger(this.metadata);
            this.cml?.initLogger(this.metadata);
            this.tel?.logTypeaheadOpen(prefill);
            this.cml?.logTypeaheadOpen(prefill);
        }
        catch (e) {
            Utils.logError(e);
        }
    }
    logTypeaheadRequest(query) {
        try {
            this.tel?.logTypeaheadRequest(query);
            this.cml?.logTypeaheadRequest(query);
        }
        catch (e) {
            Utils.logError(e);
        }
    }
    logTypeaheadResponse(response) {
        try {
            this.prevResponse = this.response;
            this.response = response;
            this.cml?.logTypeaheadResponse(response);
        }
        catch (e) {
            Utils.logError(e);
        }
    }
    logSuggestionShown(query) {
        try {
            if (query === this.response?.q) {
                this.shownResponse = this.response;
            }
            else {
                this.shownResponse = query === this.prevResponse?.q ? this.prevResponse : undefined;
            }
            if (this.shownResponse) {
                this.tel?.logTypeaheadResponse(this.shownResponse);
                this.cml?.logSuggestionShown(this.shownResponse);
            }
        }
        catch (e) {
            Utils.logError(e);
        }
    }
    logSuggestionSelected(selected) {
        try {
            if (this.shownResponse && selected) {
                // eslint-disable-next-line no-param-reassign
                if (selected.displayType === 'CATEGORIZED')
                    selected.index += 1;
                const m = this.tel?.logSuggestionSelected(selected);
                this.cml?.logSuggestionSelected(selected, m);
                this.resetResponse();
            }
            if (!selected) {
                // Then user selects resolve path
                this.tel?.isResolveNoSuggestionSelected(true);
            }
        }
        catch (e) {
            Utils.logError(e);
        }
    }
    logResponse(response) {
        try {
            if (!response.ok) {
                this.cml?.logError(response.status);
            }
            this.cml?.logResponse(response);
        }
        catch (e) {
            Utils.logError(e);
        }
    }
    logTypeaheadBlurEvent() {
        try {
            this.tel?.logTypeaheadBlurEvent();
            this.cml?.logTypeaheadClientMetrics();
            this.resetResponse();
        }
        catch (e) {
            Utils.logError(e);
        }
    }
    logWizardSubmitEvent() {
        try {
            this.tel?.logWizardSubmitEvent();
        }
        catch (e) {
            Utils.logError(e);
        }
    }
    resetResponse() {
        this.prevResponse = this.response;
        this.response = this.shownResponse;
        this.shownResponse = undefined;
    }
}

export { ESSAdapter as E, GOOGLEMAPS_CATEGORY as G, MapboxAdapter as M, PlacesServiceStatus as P, TypeaheadLocationType as T, ThirdPartyAdapterFactory as a, ThirdPartyAdapter as b, GoogleAdapter as c, TypeaheadLogger as d, appendQueryParam as e, filterTypeaheadResults as f, getSizeOfTypeaheadResults as g, appendQueryObject as h, TypeaheadRenderType as i, EGDSTypeahead as j, EGDSTypeaheadAttribution as k, EGDSTypeaheadDialog as l, EGDSTypeaheadInput as m, TypeaheadMenu as n, EGDSTypeaheadMenu as o, EGDSTypeaheadResults as p, EGDSTypeaheadResultItem as q, selectTypeaheadResultIndex as s };
//# sourceMappingURL=index-chunk.esm.js.map
